// src/pages/ContractsPage.js

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, doc, addDoc, updateDoc, deleteDoc, getDocs, orderBy, serverTimestamp } from 'firebase/firestore';
import { db } from '../App';
import { appId } from '../config/firebaseConfig';
import useConfirmationModal from '../components/common/useConfirmationModal';
import TiptapEditor from '../components/tiptap/TiptapEditor';
import SignatureModal from '../components/contracts/SignatureModal';
import { DEFAULT_CONTRACT_NAME, DEFAULT_CONTRACT_CLAUSES } from '../data/defaultContractTemplate';

export default function ContractsPage({ permissionLevel, allowedCompanies, userId, userEmail, uiItems = {} }) {
    const [activeTab, setActiveTab] = useState('contracts'); // 'contracts' | 'templates'
    const [templates, setTemplates] = useState([]);
    const [contracts, setContracts] = useState([]);
    const [clients, setClients] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [loadingContracts, setLoadingContracts] = useState(true);
    const [loadingClients, setLoadingClients] = useState(true);

    // Modais e Estados de Formulários
    const [isTemplateFormOpen, setIsTemplateFormOpen] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [templateName, setTemplateName] = useState('');
    const [templateLogo, setTemplateLogo] = useState('');
    const [templateLogoWidth, setTemplateLogoWidth] = useState(160); // Largura em px do logo
    const [templateContent, setTemplateContent] = useState('');
    
    // Estados do Construtor de Cláusulas Integrado ao Modelo
    const [modelClauses, setModelClauses] = useState([]); // Array de { id, group, title, text }
    const [clauseInputGroup, setClauseInputGroup] = useState('');
    const [isCustomGroup, setIsCustomGroup] = useState(false);
    const [customGroupName, setCustomGroupName] = useState('');
    const [clauseInputTitle, setClauseInputTitle] = useState('');
    const [clauseInputText, setClauseInputText] = useState('');
    const [isClauseComplementary, setIsClauseComplementary] = useState(false); // Se é Cláusula Complementar (ex: 10.1)
    const [editingClauseIndex, setEditingClauseIndex] = useState(null); // null quando criando nova cláusula

    // Gerenciador de Grupos / Seções do Modelo
    const [customGroups, setCustomGroups] = useState([]); // Grupos criados manualmente no modelo
    const [isManageGroupsOpen, setIsManageGroupsOpen] = useState(false);
    const [editingGroupOriginalName, setEditingGroupOriginalName] = useState(null);
    const [editGroupNewName, setEditGroupNewName] = useState('');

    const [isContractFormOpen, setIsContractFormOpen] = useState(false);
    const [currentContract, setCurrentContract] = useState(null);
    const [selectedClient, setSelectedClient] = useState('');
    const [selectedClientType, setSelectedClientType] = useState('Contrato'); // 'Contrato' | 'Avulso'
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [contractNameInput, setContractNameInput] = useState('');
    const [contractContent, setContractContent] = useState('');
    const [contractNotes, setContractNotes] = useState(''); // Comentários para a versão

    // Variáveis de Preenchimento Dinâmico
    const [contractValMaquinas, setContractValMaquinas] = useState('0');
    const [contractValMensal, setContractValMensal] = useState('0,00');
    const [ourCompanyName, setOurCompanyName] = useState('ARCUS IT NETWORK SOLUTIONS TI Ltda - ME');
    const [ourCompanyCnpj, setOurCompanyCnpj] = useState('09.407.067/0001-10');
    const [ourCompanyAddress, setOurCompanyAddress] = useState('Rua Tabajara Valeriano de Carvalho, 263, Jardim do Vale 2, Indaiatuba - SP, CEP: 13345-760');

    // Estados para Edição Autônoma de Cláusulas do Contrato Específico do Cliente
    const [contractFormActiveTab, setContractFormActiveTab] = useState('variables'); // 'variables' | 'clauses'
    const [contractClauses, setContractClauses] = useState([]); // Cláusulas exclusivas deste contrato/cliente
    const [contractLogo, setContractLogo] = useState('');
    const [contractLogoWidth, setContractLogoWidth] = useState(160);
    const [contractClauseInputGroup, setContractClauseInputGroup] = useState('');
    const [isContractCustomGroup, setIsContractCustomGroup] = useState(false);
    const [contractCustomGroupName, setContractCustomGroupName] = useState('');
    const [contractClauseInputTitle, setContractClauseInputTitle] = useState('');
    const [contractClauseInputText, setContractClauseInputText] = useState('');
    const [isContractClauseComplementary, setIsContractClauseComplementary] = useState(false); // Se é Cláusula Complementar (ex: 10.1)
    const [editingContractClauseIndex, setEditingContractClauseIndex] = useState(null);
    const [contractCustomGroups, setContractCustomGroups] = useState([]);

    // Visualização de Histórico/Detalhes
    const [viewingContract, setViewingContract] = useState(null);
    const [versions, setVersions] = useState([]);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [loadingVersions, setLoadingVersions] = useState(false);

    // Regra de Assinantes Autorizados pela Arcus
    const ARCUS_SIGNERS = useMemo(() => [
        'jonathan@arcusinformatica.com.br',
        'juliano@arcusinformatica.com.br'
    ], []);

    const isAuthorizedArcusSigner = useMemo(() => {
        if (!userEmail) return false;
        return ARCUS_SIGNERS.some(email => email.toLowerCase() === userEmail.toLowerCase().trim());
    }, [userEmail, ARCUS_SIGNERS]);

    // Modais e Estados de Assinatura
    const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
    const [signingContractTarget, setSigningContractTarget] = useState(null); // contrato sendo assinado
    const [signingVersionTarget, setSigningVersionTarget] = useState(null); // versão sendo assinada
    const [isSubmittingSignature, setIsSubmittingSignature] = useState(false);

    // Pesquisas e Filtros
    const [searchQuery, setSearchQuery] = useState('');
    const [clientTypeFilter, setClientTypeFilter] = useState('all'); // 'all' | 'Contrato' | 'Avulso'
    const [signatureStatusFilter, setSignatureStatusFilter] = useState('all'); // 'all' | 'pending' | 'signed_arcus' | 'signed_client' | 'completed'
    const [selectedClientFilter, setSelectedClientFilter] = useState('all'); // 'all' | clientId
    const [selectedContractTypeFilter, setSelectedContractTypeFilter] = useState('all'); // 'all' | templateId/name
    const [isSeedingTemplate, setIsSeedingTemplate] = useState(false);

    const { ConfirmationModal, showConfirmation } = useConfirmationModal();

    // Caminhos das coleções no Firestore
    const templatesPath = useMemo(() => `/artifacts/${appId}/public/data/contract-templates`, []);
    const contractsPath = useMemo(() => `/artifacts/${appId}/public/data/client-contracts`, []);
    const clausesPath = useMemo(() => `/artifacts/${appId}/public/data/contract-clauses`, []);
    
    // Caminhos para buscar os Clientes cadastrados para vincular ao contrato
    const clientsPath = useMemo(() => `/artifacts/intranet-arcus-prod/public/data/clients`, []);
    const avulsosPath = useMemo(() => `/artifacts/intranet-arcus-prod/public/data/clients-avulsos`, []);

    // Estado da Biblioteca de Cláusulas (Agora vinculada ao Modelo ativo)
    const [clauses, setClauses] = useState([]);
    const [isClausesManagerOpen, setIsClausesManagerOpen] = useState(false);
    const [clausesTargetTemplate, setClausesTargetTemplate] = useState(null); // Modelo do qual estamos gerenciando cláusulas
    const [editingClause, setEditingClause] = useState(null);
    const [clauseTitle, setClauseTitle] = useState('');
    const [clauseText, setClauseText] = useState('');

    // Carregar Cláusulas Dinamicamente por Modelo (seja na biblioteca do modelo ou ao editar o modelo)
    useEffect(() => {
        const activeTemplateId = clausesTargetTemplate?.id || currentTemplate?.id;
        if (!activeTemplateId) {
            setClauses([]);
            return;
        }

        const templateClausesPath = `${templatesPath}/${activeTemplateId}/clauses`;
        const q = query(collection(db, templateClausesPath), orderBy("title", "asc"));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setClauses(data);
        }, (error) => {
            console.error("Erro ao carregar cláusulas do modelo:", error);
            setClauses([]);
        });
        
        return () => unsubscribe();
    }, [clausesTargetTemplate, currentTemplate, templatesPath]);

    // 1. Carregar Clientes para o Selectbox
    useEffect(() => {
        setLoadingClients(true);
        const q1 = query(collection(db, clientsPath));
        const q2 = query(collection(db, avulsosPath));

        let clientsList = [];
        let avulsosList = [];

        const unsub1 = onSnapshot(q1, (snap) => {
            clientsList = snap.docs.map(d => {
                const data = d.data();
                return { 
                    id: d.id, 
                    name: data.name || data.nomeFantasia || data.razaoSocial || "", 
                    type: 'Contrato', 
                    originalData: data 
                };
            });
            mergeAndFilterClients(clientsList, avulsosList);
        });

        const unsub2 = onSnapshot(q2, (snap) => {
            avulsosList = snap.docs.map(d => {
                const data = d.data();
                return { 
                    id: d.id, 
                    name: data.name || data.nomeFantasia || data.razaoSocial || "", 
                    type: 'Avulso', 
                    originalData: data 
                };
            });
            mergeAndFilterClients(clientsList, avulsosList);
        });

        function mergeAndFilterClients(cList, aList) {
            let combined = [...cList, ...aList];
            if (allowedCompanies !== null) {
                combined = combined.filter(c => allowedCompanies.includes(c.name));
            }
            // Filtrar ativos
            combined = combined.filter(c => c.originalData?.ativo !== false);
            combined.sort((a, b) => {
                const nameA = a.name || a.originalData?.nomeFantasia || a.originalData?.razaoSocial || "";
                const nameB = b.name || b.originalData?.nomeFantasia || b.originalData?.razaoSocial || "";
                return nameA.localeCompare(nameB);
            });
            // Garantir que mapeamos o nome amigável para exibição
            const mapped = combined.map(c => ({
                ...c,
                name: c.name || c.originalData?.nomeFantasia || c.originalData?.razaoSocial || "Cliente Sem Nome"
            }));
            setClients(mapped);
            setLoadingClients(false);
        }

        return () => {
            unsub1();
            unsub2();
        };
    }, [clientsPath, avulsosPath, allowedCompanies]);

    // 2. Carregar Modelos Padrões
    useEffect(() => {
        const q = query(collection(db, templatesPath), orderBy("name", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setTemplates(data);
            setLoadingTemplates(false);
        }, (error) => {
            console.error("Erro ao carregar modelos:", error);
            setLoadingTemplates(false);
        });
        return () => unsubscribe();
    }, [templatesPath]);

    // 3. Carregar Contratos de Clientes
    useEffect(() => {
        const q = query(collection(db, contractsPath), orderBy("clientName", "asc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => {
                const docData = d.data();
                const matchedTemplate = templates.find(t => t.id === docData.templateId);
                const resolvedContractName = docData.contractName || docData.templateName || matchedTemplate?.name || null;
                return {
                    id: d.id,
                    ...docData,
                    contractName: resolvedContractName,
                    templateName: docData.templateName || matchedTemplate?.name || null
                };
            });
            setContracts(data);
            setLoadingContracts(false);
        }, (error) => {
            console.error("Erro ao carregar contratos:", error);
            setLoadingContracts(false);
        });
        return () => unsubscribe();
    }, [contractsPath, templates]);

    // 4. Carregar Histórico de Versões do Contrato Selecionado
    useEffect(() => {
        if (!viewingContract) {
            setVersions([]);
            setSelectedVersion(null);
            return;
        }
        setLoadingVersions(true);
        const versionsPath = `${contractsPath}/${viewingContract.id}/versions`;
        const q = query(collection(db, versionsPath), orderBy("versionNumber", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setVersions(data);
            if (data.length > 0 && !selectedVersion) {
                // Seleciona a mais recente por padrão
                setSelectedVersion(data[0]);
            } else if (data.length > 0 && selectedVersion) {
                const updated = data.find(v => v.id === selectedVersion.id);
                setSelectedVersion(updated || data[0]);
            }
            setLoadingVersions(false);
        }, (error) => {
            console.error("Erro ao carregar versões:", error);
            setLoadingVersions(false);
        });

        return () => unsubscribe();
    }, [viewingContract, contractsPath]);

    // Helper inteligente para numeração ordinal e decimal de cláusulas
    // Suporta cláusula principal (ex: Cláusula 10ª) e complementar (ex: Cláusula 10.1)
    const calculateClauseNumbering = (clausesList, index) => {
        if (!clausesList || index < 0 || index >= clausesList.length) {
            return {
                numberStr: `${index + 1}ª`,
                displayTitle: `Cláusula ${index + 1}ª`,
                isComplementary: false,
                parentIndex: null
            };
        }

        let mainCount = 0;
        let lastMainNumber = 0;
        let lastMainIndex = -1;
        let subIndex = 0;

        for (let i = 0; i <= index; i++) {
            const item = clausesList[i];
            const isComp = !!item.isComplementary;

            if (!isComp) {
                mainCount++;
                lastMainNumber = mainCount;
                lastMainIndex = i;
                subIndex = 0;
            } else {
                subIndex++;
            }

            if (i === index) {
                if (isComp && lastMainNumber > 0) {
                    return {
                        numberStr: `${lastMainNumber}.${subIndex}`,
                        displayTitle: `Cláusula ${lastMainNumber}.${subIndex}`,
                        isComplementary: true,
                        parentNumber: lastMainNumber,
                        parentIndex: lastMainIndex,
                        subIndex
                    };
                } else if (isComp && lastMainNumber === 0) {
                    // Fallback se uma complementar for a primeiríssima sem mãe
                    return {
                        numberStr: `1.${subIndex}`,
                        displayTitle: `Cláusula 1.${subIndex}`,
                        isComplementary: true,
                        parentNumber: 1,
                        parentIndex: 0,
                        subIndex
                    };
                } else {
                    return {
                        numberStr: `${mainCount}ª`,
                        displayTitle: `Cláusula ${mainCount}ª`,
                        isComplementary: false,
                        parentIndex: null
                    };
                }
            }
        }

        return {
            numberStr: `${index + 1}ª`,
            displayTitle: `Cláusula ${index + 1}ª`,
            isComplementary: false,
            parentIndex: null
        };
    };

    const getOrdinalClauseTitle = (index, clausesList = null) => {
        if (clausesList && Array.isArray(clausesList)) {
            return calculateClauseNumbering(clausesList, index).displayTitle;
        }
        const num = index + 1;
        return `Cláusula ${num}ª`;
    };

    // Helper para gerar o HTML do contrato a partir de um array de cláusulas
    const compileClausesToHtml = (clausesList, title = '', logoUrl = '', logoWidth = 160, signatures = null) => {
        if (!clausesList || clausesList.length === 0) return '';
        let html = '';
        
        // Cabeçalho superior: Logo no canto esquerdo e Título centralizado logo abaixo
        const mainTitle = title || 'CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE TECNOLOGIA DA INFORMAÇÃO (TI)';
        const safeWidth = Number(logoWidth) || 160;
        
        html += `<div style="border-bottom: 2px solid #0f172a; padding-bottom: 18px; margin-bottom: 24px;">`;
        if (logoUrl) {
            html += `<div style="margin-bottom: 16px; text-align: left;"><img src="${logoUrl}" alt="Logo" style="width: ${safeWidth}px; max-width: 100%; height: auto; object-fit: contain; display: block;" /></div>`;
        }
        html += `<h1 style="text-align: center; text-transform: uppercase; font-weight: 900; font-size: 17px; color: #0f172a; line-height: 1.4; margin: 0; letter-spacing: 0.5px;">${mainTitle}</h1>`;
        html += `</div>`;

        // Qualificação das Partes
        html += `<div style="margin-bottom: 24px; font-size: 13px; line-height: 1.6; text-align: justify; color: #1e293b;">`;
        html += `<p style="margin-bottom: 12px;"><strong>CONTRATANTE:</strong> {{NOME_CONTRATANTE}}, com sede em {{ENDERECO_CONTRATANTE}}, inscrita no CNPJ/CPF sob o nº {{CNPJ_CONTRATANTE}}, representada na forma de seus atos constitutivos.</p>`;
        html += `<p style="margin-bottom: 12px;"><strong>CONTRATADA:</strong> ARCUS IT NETWORK SOLUTIONS TI Ltda - ME, com sede em Indaiatuba, na RUA TABAJARA VALERIANO DE CARVALHO, 263, JARDIM DO VALE 2, CEP: 13345-760, no Estado SÃO PAULO, inscrita no C.N.P.J. sob o nº 09.407.067/0001-10, no Cadastro Estadual sob o nº 353.260.204.115 e CCM nº 127.402-6, neste ato representada pelo seu diretor Jonathan Henrique Dias de Oliveira, brasileiro, casado, portador da Cédula de Identidade RG nº 47.929.414-8 e CPF nº 400.323.008-05, residente e domiciliado na RUA Moema, 236, Jardim Paulista, Indaiatuba - SP.</p>`;
        html += `<p style="margin-bottom: 16px;">As partes acima identificadas têm, entre si, justas e acertadas o presente <em>Contrato de Prestação de Serviços de Manutenção e Gestão de TI</em>, que se regerá pelas cláusulas seguintes e pelas condições descritas no presente instrumento.</p>`;
        html += `</div>`;

        // Cláusulas Organizadas por Grupo
        let currentGroup = null;
        clausesList.forEach((c, idx) => {
            const groupName = c.group ? c.group.trim() : '';

            // Se mudou de grupo e existe um grupo definido, adiciona o título do Grupo em destaque
            if (groupName && groupName !== currentGroup) {
                currentGroup = groupName;
                html += `<div style="margin-top: 24px; margin-bottom: 12px; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px;">`;
                html += `<h2 style="font-weight: 900; font-size: 14px; text-transform: uppercase; color: #0f172a; margin: 0; letter-spacing: 0.5px;">${groupName}</h2>`;
                html += `</div>`;
            } else if (!groupName && currentGroup !== null) {
                currentGroup = null;
            }

            // Título inteligente e padronizado da cláusula (ex: Cláusula 10ª ou Cláusula 10.1)
            const numbering = calculateClauseNumbering(clausesList, idx);
            const dynamicTitle = numbering.displayTitle;
            const extraSubtitle = c.title && !/^cl[áa]usula\s*\d+[ªº°\.]?\d*/i.test(c.title.trim()) ? ` - ${c.title.trim()}` : '';
            const fullTitleDisplay = `${dynamicTitle}${extraSubtitle}`;

            const isComp = numbering.isComplementary;
            const containerStyle = isComp 
                ? `margin-bottom: 14px; margin-left: 20px; padding-left: 14px; border-left: 2px solid #cbd5e1;` 
                : `margin-bottom: 16px;`;

            html += `<div style="${containerStyle}">`;
            html += `<span style="font-weight: 800; font-size: 13px; color: #0f172a; margin-right: 8px; text-transform: uppercase;">${fullTitleDisplay}.</span>`;
            if (c.text) {
                const paragraphs = c.text.split('\n').filter(p => p.trim() !== '');
                if (paragraphs.length > 1) {
                    paragraphs.forEach((p, pIdx) => {
                        if (pIdx === 0 && c.title) {
                            html += `<span style="line-height: 1.6; font-size: 13px; text-align: justify; color: #334155;">${p}</span>`;
                        } else {
                            html += `<p style="margin-top: 6px; margin-bottom: 6px; line-height: 1.6; font-size: 13px; text-align: justify; color: #334155;">${p}</p>`;
                        }
                    });
                } else {
                    html += `<span style="line-height: 1.6; font-size: 13px; text-align: justify; color: #334155;">${c.text}</span>`;
                }
            }
            html += `</div>`;
        });

        // Fecho do Contrato
        const dataAtualStr = new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' });
        html += `<div style="margin-top: 36px; font-size: 13px; line-height: 1.6; text-align: justify; color: #1e293b;">`;
        html += `<p style="margin-bottom: 16px;">Por estarem assim justos e contratados, firmam o presente instrumento em vias eletrônicas de igual teor e validade jurídica.</p>`;
        html += `<p style="text-align: center; margin-top: 24px; margin-bottom: 30px; font-weight: 700; color: #0f172a; font-size: 13px;">Indaiatuba, ${dataAtualStr}.</p>`;
        html += `</div>`;

        // Quadro de Assinaturas (com suporte a Assinatura Eletrônica Oficial)
        const arcusSig = signatures?.arcus;
        const clientSig = signatures?.client;

        html += `<div style="margin-top: 30px; display: grid; grid-template-columns: 1fr 1fr; gap: 30px; text-align: center; font-size: 11px; color: #334155;">`;
        
        // Assinatura CONTRATADA (Arcus)
        html += `<div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; justify-content: space-between; min-height: 160px;">`;
        if (arcusSig) {
            html += `<div>`;
            if (arcusSig.signatureBase64) {
                html += `<div style="margin-bottom: 8px; text-align: center;"><img src="${arcusSig.signatureBase64}" alt="Assinatura Arcus" style="max-height: 55px; max-width: 190px; margin: 0 auto; display: block;" /></div>`;
            }
            html += `<div style="display: inline-block; background-color: #ecfdf5; border: 1px solid #a7f3d0; color: #047857; font-size: 9px; font-weight: 800; padding: 2px 8px; border-radius: 9999px; text-transform: uppercase; margin-bottom: 6px;">✓ Assinado Eletronicamente</div>`;
            html += `<p style="font-weight: 800; color: #0f172a; font-size: 11px; margin: 2px 0;">${arcusSig.signerName || 'ARCUS TECNOLOGIA'}</p>`;
            html += `<p style="font-size: 9.5px; color: #64748b; margin: 2px 0;">${arcusSig.signerEmail || ''} ${arcusSig.signerDocument ? `• ${arcusSig.signerDocument}` : ''}</p>`;
            html += `<p style="font-size: 8.5px; font-family: monospace; color: #94a3b8; margin: 4px 0 0 0;">Data: ${arcusSig.signedAt ? new Date(arcusSig.signedAt).toLocaleString('pt-BR') : ''}</p>`;
            html += `</div>`;
        } else {
            html += `<div style="border-top: 1px solid #94a3b8; margin-top: 55px; padding-top: 8px;">`;
            html += `<span style="display: inline-block; background-color: #fef3c7; color: #b45309; font-size: 8.5px; font-weight: 700; padding: 1px 6px; border-radius: 4px; margin-bottom: 6px;">Pendente de Assinatura</span><br/>`;
            html += `</div>`;
        }
        html += `<div style="border-top: 1px solid #cbd5e1; margin-top: 10px; padding-top: 6px;"><strong>ARCUS IT NETWORK SOLUTIONS TI Ltda - ME</strong><br/><span style="font-size: 9.5px; color: #64748b; font-weight: 600;">CONTRATADA</span></div>`;
        html += `</div>`;

        // Assinatura CONTRATANTE (Cliente)
        html += `<div style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; display: flex; flex-direction: column; justify-content: space-between; min-height: 160px;">`;
        if (clientSig) {
            html += `<div>`;
            if (clientSig.signatureBase64) {
                html += `<div style="margin-bottom: 8px; text-align: center;"><img src="${clientSig.signatureBase64}" alt="Assinatura Cliente" style="max-height: 55px; max-width: 190px; margin: 0 auto; display: block;" /></div>`;
            }
            html += `<div style="display: inline-block; background-color: #ecfdf5; border: 1px solid #a7f3d0; color: #047857; font-size: 9px; font-weight: 800; padding: 2px 8px; border-radius: 9999px; text-transform: uppercase; margin-bottom: 6px;">✓ Assinado Eletronicamente</div>`;
            html += `<p style="font-weight: 800; color: #0f172a; font-size: 11px; margin: 2px 0;">${clientSig.signerName || 'CONTRATANTE'}</p>`;
            html += `<p style="font-size: 9.5px; color: #64748b; margin: 2px 0;">${clientSig.signerEmail || ''} ${clientSig.signerDocument ? `• ${clientSig.signerDocument}` : ''}</p>`;
            html += `<p style="font-size: 8.5px; font-family: monospace; color: #94a3b8; margin: 4px 0 0 0;">Data: ${clientSig.signedAt ? new Date(clientSig.signedAt).toLocaleString('pt-BR') : ''}</p>`;
            html += `</div>`;
        } else {
            html += `<div style="border-top: 1px solid #94a3b8; margin-top: 55px; padding-top: 8px;">`;
            html += `<span style="display: inline-block; background-color: #fef3c7; color: #b45309; font-size: 8.5px; font-weight: 700; padding: 1px 6px; border-radius: 4px; margin-bottom: 6px;">Pendente de Assinatura</span><br/>`;
            html += `</div>`;
        }
        html += `<div style="border-top: 1px solid #cbd5e1; margin-top: 10px; padding-top: 6px;"><strong>{{NOME_CONTRATANTE}}</strong><br/><span style="font-size: 9.5px; color: #64748b; font-weight: 600;">CONTRATANTE</span></div>`;
        html += `</div>`;
        html += `</div>`;

        return html;
    };

    // --- AÇÕES DE MODELOS ---
    const handleOpenTemplateForm = async (template = null) => {
        if (template) {
            setCurrentTemplate(template);
            setTemplateName(template.name);
            setTemplateLogo(template.logo || '');
            setTemplateLogoWidth(template.logoWidth || 160);
            setTemplateContent(template.content || '');
            
            // Carregar cláusulas se já existirem no documento
            if (template.clauses && Array.isArray(template.clauses) && template.clauses.length > 0) {
                setModelClauses(template.clauses);
            } else {
                // Tentar buscar da subcoleção de cláusulas deste modelo se houver
                try {
                    const templateClausesPath = `${templatesPath}/${template.id}/clauses`;
                    const snap = await getDocs(query(collection(db, templateClausesPath), orderBy("title", "asc")));
                    if (!snap.empty) {
                        const loaded = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                        setModelClauses(loaded);
                    } else if (template.content) {
                        // Caso seja um modelo antigo legado que só tinha HTML direto
                        setModelClauses([{
                            id: 'legacy-1',
                            title: 'TEXTO INTEGRAL DO MODELO',
                            text: template.content.replace(/<[^>]+>/g, '\n').replace(/\n+/g, '\n').trim()
                        }]);
                    } else {
                        setModelClauses([]);
                    }
                } catch (err) {
                    console.error("Erro ao carregar cláusulas do modelo:", err);
                    setModelClauses([]);
                }
            }
        } else {
            setCurrentTemplate(null);
            setTemplateName('');
            setTemplateLogo('');
            setTemplateLogoWidth(160);
            setTemplateContent('');
            setModelClauses([]);
        }

        setCustomGroups([]);
        setIsManageGroupsOpen(false);
        setEditingGroupOriginalName(null);
        setEditGroupNewName('');
        setClauseInputGroup('');
        setIsCustomGroup(false);
        setCustomGroupName('');
        setClauseInputTitle('');
        setClauseInputText('');
        setEditingClauseIndex(null);
        setIsTemplateFormOpen(true);
    };

    // Manipuladores de Cláusulas dentro do Modelo
    const handleAddOrUpdateModelClause = (e) => {
        if (e) e.preventDefault();
        if (!clauseInputText.trim() && !clauseInputTitle.trim()) return;

        // Determina o grupo alvo: se for customGroup usa o digitado, caso contrário usa a seleção
        let targetGroup = '';
        if (isCustomGroup) {
            targetGroup = customGroupName.trim().toUpperCase();
        } else {
            targetGroup = clauseInputGroup.trim().toUpperCase();
        }

        let updated = [...modelClauses];

        if (editingClauseIndex !== null && editingClauseIndex >= 0 && editingClauseIndex < updated.length) {
            // Atualizar cláusula existente
            updated[editingClauseIndex] = {
                ...updated[editingClauseIndex],
                group: targetGroup,
                title: clauseInputTitle.trim(),
                text: clauseInputText.trim(),
                isComplementary: !!isClauseComplementary
            };
        } else {
            // Adicionar nova cláusula
            const newClause = {
                id: `clause_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                group: targetGroup,
                title: clauseInputTitle.trim(),
                text: clauseInputText.trim(),
                isComplementary: !!isClauseComplementary
            };

            // Inserção inteligente: se o grupo já existe, insere logo após a última cláusula desse grupo
            if (targetGroup) {
                let lastIndexInGroup = -1;
                for (let i = updated.length - 1; i >= 0; i--) {
                    if ((updated[i].group || '').trim() === targetGroup) {
                        lastIndexInGroup = i;
                        break;
                    }
                }

                if (lastIndexInGroup !== -1) {
                    updated.splice(lastIndexInGroup + 1, 0, newClause);
                } else {
                    updated.push(newClause);
                }
            } else {
                updated.push(newClause);
            }
        }

        setModelClauses(updated);
        // Registra o grupo em customGroups se já não estiver registrado
        if (targetGroup) {
            setCustomGroups(prev => {
                const upper = targetGroup.toUpperCase();
                if (!prev.some(g => g.toUpperCase() === upper)) {
                    return [...prev, targetGroup];
                }
                return prev;
            });
        }
        // Preserva o grupo atual selecionado na lista para facilitar adicionar várias cláusulas na mesma seção
        setClauseInputGroup(targetGroup);
        setIsCustomGroup(false);
        setCustomGroupName('');
        setClauseInputTitle('');
        setClauseInputText('');
        setIsClauseComplementary(false);
        setEditingClauseIndex(null);
    };

    const handleEditModelClause = (index) => {
        const item = modelClauses[index];
        if (!item) return;
        setEditingClauseIndex(index);
        setClauseInputGroup(item.group || '');
        setIsCustomGroup(false);
        setCustomGroupName('');
        setClauseInputTitle(item.title || '');
        setClauseInputText(item.text || '');
        setIsClauseComplementary(!!item.isComplementary);
    };

    const handleAddComplementaryToModel = (parentIndex) => {
        const parent = modelClauses[parentIndex];
        if (!parent) return;
        setEditingClauseIndex(null);
        setClauseInputGroup(parent.group || '');
        setIsCustomGroup(false);
        setCustomGroupName('');
        setClauseInputTitle('');
        setClauseInputText('');
        setIsClauseComplementary(true);
    };

    const handleCancelEditClause = () => {
        setEditingClauseIndex(null);
        setClauseInputGroup('');
        setIsCustomGroup(false);
        setCustomGroupName('');
        setClauseInputTitle('');
        setClauseInputText('');
        setIsClauseComplementary(false);
    };

    const handleDeleteModelClause = (index) => {
        const updated = modelClauses.filter((_, i) => i !== index);
        setModelClauses(updated);
        if (editingClauseIndex === index) {
            handleCancelEditClause();
        } else if (editingClauseIndex !== null && editingClauseIndex > index) {
            setEditingClauseIndex(editingClauseIndex - 1);
        }
    };

    const handleMoveClause = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= modelClauses.length) return;
        const updated = [...modelClauses];
        const temp = updated[index];
        updated[index] = updated[targetIndex];
        updated[targetIndex] = temp;
        setModelClauses(updated);
        if (editingClauseIndex === index) {
            setEditingClauseIndex(targetIndex);
        } else if (editingClauseIndex === targetIndex) {
            setEditingClauseIndex(index);
        }
    };

    const handleInsertVariableIntoClause = (placeholder) => {
        setClauseInputText(prev => prev ? `${prev} ${placeholder}` : placeholder);
    };

    // --- GERENCIAMENTO DE GRUPOS / SEÇÕES ---
    const handleStartEditGroup = (groupName) => {
        setEditingGroupOriginalName(groupName);
        setEditGroupNewName(groupName);
    };

    const handleSaveEditGroup = (originalName) => {
        const trimmedNewName = editGroupNewName.trim().toUpperCase();
        if (!trimmedNewName) {
            alert("O nome do grupo não pode ficar vazio.");
            return;
        }

        // Atualiza todas as cláusulas que usavam o nome antigo
        const updatedClauses = modelClauses.map(cl => {
            if ((cl.group || '').trim().toUpperCase() === originalName.toUpperCase()) {
                return { ...cl, group: trimmedNewName };
            }
            return cl;
        });
        setModelClauses(updatedClauses);

        // Atualiza na lista de grupos criados
        setCustomGroups(prev => {
            const next = prev.filter(g => g.toUpperCase() !== originalName.toUpperCase());
            if (!next.includes(trimmedNewName)) next.push(trimmedNewName);
            return next;
        });

        // Se o grupo atualmente selecionado no form era o editado, atualiza
        if (clauseInputGroup.toUpperCase() === originalName.toUpperCase()) {
            setClauseInputGroup(trimmedNewName);
        }

        setEditingGroupOriginalName(null);
        setEditGroupNewName('');
    };

    const handleDeleteGroup = (groupName) => {
        const normalized = groupName.trim().toUpperCase();
        const clausesCount = modelClauses.filter(cl => (cl.group || '').trim().toUpperCase() === normalized).length;

        if (clausesCount > 0) {
            showConfirmation({
                title: "Não é possível excluir o grupo",
                message: `O grupo "${normalized}" possui ${clausesCount} cláusula(s) vinculada(s). Remova ou altere as cláusulas deste grupo primeiro para poder excluí-lo.`,
                confirmText: "Entendi",
                confirmColor: "bg-amber-600",
                showCancelButton: false,
                onConfirm: () => {}
            });
            return;
        }

        showConfirmation({
            title: "Excluir Grupo / Seção?",
            message: `Deseja remover o grupo "${normalized}" da lista de grupos deste modelo?`,
            confirmText: "Excluir Grupo",
            confirmColor: "bg-red-600",
            showCancelButton: true,
            onConfirm: () => {
                setCustomGroups(prev => prev.filter(g => g.toUpperCase() !== normalized));
                if (clauseInputGroup.toUpperCase() === normalized) {
                    setClauseInputGroup('');
                }
            }
        });
    };

    const handleSaveTemplate = async (e) => {
        e.preventDefault();
        if (!templateName.trim()) return;

        try {
            // Gera o HTML compilado com base nas cláusulas do modelo, logo e largura ajustada
            const compiledHtml = compileClausesToHtml(modelClauses, templateName, templateLogo, templateLogoWidth);

            const docData = {
                name: templateName,
                logo: templateLogo || '',
                logoWidth: Number(templateLogoWidth) || 160,
                clauses: modelClauses,
                content: compiledHtml,
                updatedAt: serverTimestamp(),
                updatedBy: userId
            };

            let targetTemplateId = currentTemplate?.id;

            if (currentTemplate) {
                await updateDoc(doc(db, templatesPath, currentTemplate.id), docData);
            } else {
                docData.createdAt = serverTimestamp();
                const newDocRef = await addDoc(collection(db, templatesPath), docData);
                targetTemplateId = newDocRef.id;
            }

            // Sincronizar também com a subcoleção de cláusulas se aplicável
            if (targetTemplateId && modelClauses.length > 0) {
                try {
                    const templateClausesPath = `${templatesPath}/${targetTemplateId}/clauses`;
                    // Para garantir consistência com eventuais visualizadores legados
                    for (const cl of modelClauses) {
                        if (cl.id && !cl.id.startsWith('clause_')) {
                            await updateDoc(doc(db, templateClausesPath, cl.id), {
                                title: cl.title,
                                text: cl.text,
                                updatedAt: serverTimestamp()
                            }).catch(() => {});
                        }
                    }
                } catch (err) {
                    console.log("Subcoleção sync info:", err);
                }
            }

            setIsTemplateFormOpen(false);
            setCurrentTemplate(null);
            setTemplateName('');
            setTemplateLogo('');
            setTemplateContent('');
            setModelClauses([]);
            setEditingClauseIndex(null);
        } catch (error) {
            console.error("Erro ao salvar modelo:", error);
            alert("Erro ao salvar o modelo de contrato.");
        }
    };

    // Criação direta do modelo padrão oficial Arcus no banco de dados com 1 clique
    const handleSeedDefaultTemplate = async () => {
        setIsSeedingTemplate(true);
        try {
            const compiledHtml = compileClausesToHtml(DEFAULT_CONTRACT_CLAUSES, DEFAULT_CONTRACT_NAME);
            const docData = {
                name: DEFAULT_CONTRACT_NAME,
                clauses: DEFAULT_CONTRACT_CLAUSES,
                content: compiledHtml,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                updatedBy: userId || 'system'
            };

            const docRef = await addDoc(collection(db, templatesPath), docData);

            // Sincronizar cláusulas na subcoleção para compatibilidade
            const templateClausesPath = `${templatesPath}/${docRef.id}/clauses`;
            for (const cl of DEFAULT_CONTRACT_CLAUSES) {
                await addDoc(collection(db, templateClausesPath), {
                    title: cl.title,
                    text: cl.text,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                }).catch(() => {});
            }

            alert("Modelo padrão oficial de TI da Arcus criado com sucesso com as 22 cláusulas!");
        } catch (error) {
            console.error("Erro ao carregar modelo padrão:", error);
            alert("Erro ao carregar o modelo padrão. Verifique o console.");
        } finally {
            setIsSeedingTemplate(false);
        }
    };

    // Carregar todas as 22 cláusulas padrão diretamente para o editor de modelo aberto
    const handleLoadOfficialClausesIntoEditor = () => {
        if (!templateName) {
            setTemplateName(DEFAULT_CONTRACT_NAME);
        }
        setModelClauses(DEFAULT_CONTRACT_CLAUSES);
    };

    const handleDeleteTemplate = (template) => {
        showConfirmation({
            title: "Excluir Modelo?",
            message: `Tem certeza que deseja apagar o modelo "${template.name}"? Esta ação é irreversível.`,
            confirmText: "Excluir",
            confirmColor: "bg-red-600",
            showCancelButton: true,
            onConfirm: async () => {
                try {
                    await deleteDoc(doc(db, templatesPath, template.id));
                } catch (error) {
                    console.error("Erro ao excluir modelo:", error);
                }
            }
        });
    };

    // --- AÇÕES DE CONTRATOS ---
    const handleOpenContractForm = async (contract = null) => {
        // Reset tab e inputs de cláusula do contrato
        setContractFormActiveTab('variables');
        setContractClauseInputGroup('');
        setIsContractCustomGroup(false);
        setContractCustomGroupName('');
        setContractClauseInputTitle('');
        setContractClauseInputText('');
        setEditingContractClauseIndex(null);
        setContractCustomGroups([]);

        if (contract) {
            setCurrentContract(contract);
            setSelectedClient(contract.clientId);
            setSelectedClientType(contract.clientType || 'Contrato');
            
            // Tenta encontrar o template id correspondente
            let matchedTemplateId = contract.templateId || '';
            if (!matchedTemplateId && contract.templateName) {
                const foundTpl = templates.find(t => t.name === contract.templateName);
                if (foundTpl) matchedTemplateId = foundTpl.id;
            }
            setSelectedTemplate(matchedTemplateId);
            setContractNameInput(contract.contractName || contract.templateName || '');
            setContractNotes('Revisão do contrato');
            setContractLogo(contract.logo || '');
            setContractLogoWidth(contract.logoWidth || 160);

            // Carrega as cláusulas existentes do contrato, ou clona do modelo se não houver
            let cClauses = contract.clauses || [];
            if ((!cClauses || cClauses.length === 0) && matchedTemplateId) {
                const matchedTpl = templates.find(t => t.id === matchedTemplateId);
                if (matchedTpl?.clauses && matchedTpl.clauses.length > 0) {
                    cClauses = JSON.parse(JSON.stringify(matchedTpl.clauses));
                } else {
                    try {
                        const snap = await getDocs(query(collection(db, `${templatesPath}/${matchedTemplateId}/clauses`), orderBy("title", "asc")));
                        if (!snap.empty) {
                            cClauses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                        }
                    } catch (err) {
                        console.error("Erro ao buscar cláusulas do modelo para o contrato:", err);
                    }
                }
                if ((!cClauses || cClauses.length === 0) && (matchedTpl?.name === DEFAULT_CONTRACT_NAME || matchedTpl?.name?.toLowerCase().includes('ti'))) {
                    cClauses = JSON.parse(JSON.stringify(DEFAULT_CONTRACT_CLAUSES));
                }
            }

            setContractClauses(cClauses);

            // Compilar conteúdo com as cláusulas do contrato
            const client = clients.find(c => c.id === contract.clientId);
            if (cClauses.length > 0) {
                const raw = compileClausesToHtml(cClauses, contract.contractName || contract.templateName, contract.logo, contract.logoWidth);
                setContractContent(compileContractContent(raw, client, contractValMaquinas, contractValMensal, ourCompanyName, ourCompanyCnpj, ourCompanyAddress));
            } else {
                setContractContent(contract.content || '');
            }
        } else {
            setCurrentContract(null);
            setSelectedClient('');
            setSelectedClientType('Contrato');
            setSelectedTemplate('');
            setContractNameInput('');
            setContractContent('');
            setContractNotes('Criação inicial do contrato');
            setContractClauses([]);
            setContractLogo('');
            setContractLogoWidth(160);
        }
        setIsContractFormOpen(true);
    };

    // Helper para processar/compilar o texto do template aplicando os dados das variáveis
    const compileContractContent = (rawTemplateContent, client, valMaquinas, valMensal, companyName, companyCnpj, companyAddress) => {
        if (!rawTemplateContent) return '';
        
        let clientName = '';
        let clientCnpj = '';
        let clientAddress = '';

        if (client && client.originalData) {
            clientName = client.name || '';
            clientCnpj = client.originalData.cnpj || client.originalData.cpf || '';
            
            const r = client.originalData;
            const logradouro = r.logradouro || r.endereco || '';
            const numero = r.numero || '';
            const bairro = r.bairro || '';
            const cidade = r.cidade || '';
            const uf = r.uf || r.estado || '';
            const cep = r.cep || '';
            
            clientAddress = `${logradouro}${numero ? `, ${numero}` : ''}${bairro ? `, ${bairro}` : ''}${cidade ? `, ${cidade}` : ''}${uf ? ` - ${uf}` : ''}${cep ? ` (CEP: ${cep})` : ''}`;
        }

        let compiled = rawTemplateContent;
        // Substituir dados da nossa contratada
        compiled = compiled.replace(/\{\{NOME_CONTRATADA\}\}/g, companyName);
        compiled = compiled.replace(/\{\{CNPJ_CONTRATADA\}\}/g, companyCnpj);
        compiled = compiled.replace(/\{\{ENDERECO_CONTRATADA\}\}/g, companyAddress);
        
        // Substituir dados do cliente contratante
        compiled = compiled.replace(/\{\{NOME_CONTRATANTE\}\}/g, clientName);
        compiled = compiled.replace(/\{\{CNPJ_CONTRATANTE\}\}/g, clientCnpj);
        compiled = compiled.replace(/\{\{ENDERECO_CONTRATANTE\}\}/g, clientAddress);

        // Substituir variáveis pontuais
        compiled = compiled.replace(/\{\{QTD_MAQUINAS\}\}/g, valMaquinas);
        compiled = compiled.replace(/\{\{VALOR_MENSAL\}\}/g, valMensal);

        return compiled;
    };

    // Recompila o HTML do contrato usando as cláusulas independentes atuais (contractClauses)
    const recompileContractWithClauses = (
        currentClauses = contractClauses,
        cId = selectedClient,
        title = contractNameInput,
        logo = contractLogo,
        logoW = contractLogoWidth,
        valMaq = contractValMaquinas,
        valMen = contractValMensal,
        compName = ourCompanyName,
        compCnpj = ourCompanyCnpj,
        compAddr = ourCompanyAddress
    ) => {
        const client = clients.find(c => c.id === cId);
        const finalTitle = title || 'Contrato de Prestação de Serviços';
        const raw = compileClausesToHtml(currentClauses, finalTitle, logo, logoW);
        const compiled = compileContractContent(raw, client, valMaq, valMen, compName, compCnpj, compAddr);
        setContractContent(compiled);
    };

    // Copia cláusulas do modelo selecionado como gabarito inicial independente
    const handleApplyTemplateVariables = async (tId = selectedTemplate, cId = selectedClient, valMaq = contractValMaquinas, valMen = contractValMensal, compName = ourCompanyName, compCnpj = ourCompanyCnpj, compAddr = ourCompanyAddress) => {
        if (!tId) return;
        let template = templates.find(t => t.id === tId);
        const client = clients.find(c => c.id === cId);
        if (!template) return;

        // Se o modelo não possui cláusulas em memória direta no documento, busca da subcoleção de cláusulas
        let tClauses = template.clauses || [];
        if ((!tClauses || tClauses.length === 0) && template.id) {
            try {
                const templateClausesPath = `${templatesPath}/${template.id}/clauses`;
                const snap = await getDocs(query(collection(db, templateClausesPath), orderBy("title", "asc")));
                if (!snap.empty) {
                    tClauses = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                }
            } catch (err) {
                console.error("Erro ao buscar cláusulas do modelo para o contrato:", err);
            }
        }

        // Se mesmo assim as cláusulas estiverem vazias e o modelo for o oficial de TI da Arcus, usa DEFAULT_CONTRACT_CLAUSES como fallback
        if ((!tClauses || tClauses.length === 0) && (template.name === DEFAULT_CONTRACT_NAME || template.name?.toLowerCase().includes('tecnologia da informação') || template.name?.toLowerCase().includes('ti'))) {
            tClauses = DEFAULT_CONTRACT_CLAUSES;
        }

        // Clona de forma profunda para garantir independência total
        const clonedClauses = JSON.parse(JSON.stringify(tClauses || []));
        setContractClauses(clonedClauses);

        const chosenLogo = template.logo || contractLogo || '';
        const chosenLogoWidth = template.logoWidth || contractLogoWidth || 160;
        setContractLogo(chosenLogo);
        setContractLogoWidth(chosenLogoWidth);

        // Gera o HTML compilado com logotipo e layout oficial
        const finalTitle = contractNameInput || template.name || 'Contrato de Prestação de Serviços';
        const rawContent = compileClausesToHtml(clonedClauses, finalTitle, chosenLogo, chosenLogoWidth);
        const compiled = compileContractContent(rawContent, client, valMaq, valMen, compName, compCnpj, compAddr);
        setContractContent(compiled);
    };

    // Handlers para re-compilar dinamicamente
    const handleTemplateChange = (e) => {
        const tId = e.target.value;
        setSelectedTemplate(tId);
        const tpl = templates.find(t => t.id === tId);
        let newTitle = contractNameInput;
        if (tpl && (!contractNameInput || contractNameInput === 'Contrato de Prestação de Serviços')) {
            newTitle = tpl.name || '';
            setContractNameInput(newTitle);
        }
        handleApplyTemplateVariables(tId, selectedClient, contractValMaquinas, contractValMensal);
    };

    const handleClientChange = (e) => {
        const cId = e.target.value;
        setSelectedClient(cId);
        recompileContractWithClauses(contractClauses, cId);
    };

    const handleVariablesChange = (valMaq, valMen) => {
        setContractValMaquinas(valMaq);
        setContractValMensal(valMen);
        recompileContractWithClauses(contractClauses, selectedClient, contractNameInput, contractLogo, contractLogoWidth, valMaq, valMen);
    };

    // --- MANIPULADORES DE CLÁUSULAS ESPECÍFICAS DESTE CONTRATO (INDEPENDÊNCIA TOTAL) ---
    const handleAddOrUpdateContractClause = (e) => {
        if (e) e.preventDefault();
        if (!contractClauseInputText.trim() && !contractClauseInputTitle.trim()) return;

        let targetGroup = '';
        if (isContractCustomGroup) {
            targetGroup = contractCustomGroupName.trim().toUpperCase();
        } else {
            targetGroup = contractClauseInputGroup.trim().toUpperCase();
        }

        let updated = [...contractClauses];

        if (editingContractClauseIndex !== null && editingContractClauseIndex >= 0 && editingContractClauseIndex < updated.length) {
            // Atualizar cláusula existente
            updated[editingContractClauseIndex] = {
                ...updated[editingContractClauseIndex],
                group: targetGroup,
                title: contractClauseInputTitle.trim(),
                text: contractClauseInputText.trim(),
                isComplementary: !!isContractClauseComplementary
            };
        } else {
            // Adicionar nova cláusula
            const newClause = {
                id: `cclause_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                group: targetGroup,
                title: contractClauseInputTitle.trim(),
                text: contractClauseInputText.trim(),
                isComplementary: !!isContractClauseComplementary
            };

            // Inserção inteligente: se o grupo já existe, insere logo após a última cláusula desse grupo
            if (targetGroup) {
                let lastIndexInGroup = -1;
                for (let i = updated.length - 1; i >= 0; i--) {
                    if ((updated[i].group || '').trim() === targetGroup) {
                        lastIndexInGroup = i;
                        break;
                    }
                }
                if (lastIndexInGroup !== -1) {
                    updated.splice(lastIndexInGroup + 1, 0, newClause);
                } else {
                    updated.push(newClause);
                }
            } else {
                updated.push(newClause);
            }
        }

        setContractClauses(updated);

        // Registra o grupo em contractCustomGroups se novo
        if (targetGroup) {
            setContractCustomGroups(prev => {
                const upper = targetGroup.toUpperCase();
                if (!prev.some(g => g.toUpperCase() === upper)) {
                    return [...prev, targetGroup];
                }
                return prev;
            });
        }

        setContractClauseInputGroup(targetGroup);
        setIsContractCustomGroup(false);
        setContractCustomGroupName('');
        setContractClauseInputTitle('');
        setContractClauseInputText('');
        setIsContractClauseComplementary(false);
        setEditingContractClauseIndex(null);

        // Atualiza a visualização em tempo real da folha A4
        recompileContractWithClauses(updated);
    };

    const handleEditContractClause = (index) => {
        const item = contractClauses[index];
        if (!item) return;
        setEditingContractClauseIndex(index);
        setContractClauseInputGroup(item.group || '');
        setIsContractCustomGroup(false);
        setContractCustomGroupName('');
        setContractClauseInputTitle(item.title || '');
        setContractClauseInputText(item.text || '');
        setIsContractClauseComplementary(!!item.isComplementary);
    };

    const handleAddComplementaryToContract = (parentIndex) => {
        const parent = contractClauses[parentIndex];
        if (!parent) return;
        setEditingContractClauseIndex(null);
        setContractClauseInputGroup(parent.group || '');
        setIsContractCustomGroup(false);
        setContractCustomGroupName('');
        setContractClauseInputTitle('');
        setContractClauseInputText('');
        setIsContractClauseComplementary(true);
    };

    const handleCancelEditContractClause = () => {
        setEditingContractClauseIndex(null);
        setContractClauseInputGroup('');
        setIsContractCustomGroup(false);
        setContractCustomGroupName('');
        setContractClauseInputTitle('');
        setContractClauseInputText('');
        setIsContractClauseComplementary(false);
    };

    const handleDeleteContractClause = (index) => {
        const updated = contractClauses.filter((_, i) => i !== index);
        setContractClauses(updated);
        if (editingContractClauseIndex === index) {
            handleCancelEditContractClause();
        } else if (editingContractClauseIndex !== null && editingContractClauseIndex > index) {
            setEditingContractClauseIndex(editingContractClauseIndex - 1);
        }
        recompileContractWithClauses(updated);
    };

    const handleMoveContractClause = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= contractClauses.length) return;
        const updated = [...contractClauses];
        const temp = updated[index];
        updated[index] = updated[targetIndex];
        updated[targetIndex] = temp;
        setContractClauses(updated);
        if (editingContractClauseIndex === index) {
            setEditingContractClauseIndex(targetIndex);
        } else if (editingContractClauseIndex === targetIndex) {
            setEditingContractClauseIndex(index);
        }
        recompileContractWithClauses(updated);
    };

    const handleInsertVariableIntoContractClause = (placeholder) => {
        setContractClauseInputText(prev => prev ? `${prev} ${placeholder}` : placeholder);
    };

    const handleSaveContract = async (e) => {
        e.preventDefault();
        if (!selectedClient) {
            alert("Selecione um cliente.");
            return;
        }

        const clientObj = clients.find(c => c.id === selectedClient);
        if (!clientObj) return;

        const selectedTemplateObj = templates.find(t => t.id === selectedTemplate);
        const finalContractName = contractNameInput.trim() || selectedTemplateObj?.name || 'Contrato de Prestação de Serviços';

        // Logotipo e cláusulas específicas deste contrato (sem alterar o modelo base de partida)
        const finalLogo = contractLogo || selectedTemplateObj?.logo || '';
        const finalLogoWidth = Number(contractLogoWidth) || Number(selectedTemplateObj?.logoWidth) || 160;
        const clausesToPersist = contractClauses.length > 0 ? contractClauses : (selectedTemplateObj?.clauses || []);

        try {
            if (currentContract) {
                // Atualizar contrato existente e salvar uma nova versão
                const nextVersionNumber = (currentContract.currentVersion || 1) + 1;
                const contractRef = doc(db, contractsPath, currentContract.id);
                
                const updatePayload = {
                    contractName: finalContractName,
                    content: contractContent,
                    currentVersion: nextVersionNumber,
                    clauses: clausesToPersist,
                    logo: finalLogo,
                    logoWidth: finalLogoWidth,
                    updatedAt: serverTimestamp(),
                    updatedBy: userId
                };

                await updateDoc(contractRef, updatePayload);

                // Adicionar documento de versão na subcoleção com cópia independente das cláusulas
                const versionsColRef = collection(db, `${contractsPath}/${currentContract.id}/versions`);
                await addDoc(versionsColRef, {
                    versionNumber: nextVersionNumber,
                    content: contractContent,
                    clauses: clausesToPersist,
                    notes: contractNotes || 'Alteração sem comentários',
                    savedAt: serverTimestamp(),
                    savedBy: userId
                });

                // Atualizar o viewingContract se estiver aberto para ver a nova versão
                if (viewingContract && viewingContract.id === currentContract.id) {
                    setViewingContract(prev => ({
                        ...prev,
                        contractName: finalContractName,
                        content: contractContent,
                        currentVersion: nextVersionNumber,
                        clauses: clausesToPersist,
                        logo: finalLogo,
                        logoWidth: finalLogoWidth
                    }));
                }

            } else {
                // Criar novo contrato com suas próprias cláusulas independentes
                const newDocData = {
                    clientId: selectedClient,
                    clientName: clientObj.name,
                    clientType: clientObj.type, // Salvar se é Contrato ou Avulso
                    templateId: selectedTemplate || null,
                    templateName: selectedTemplateObj?.name || null,
                    contractName: finalContractName,
                    content: contractContent,
                    clauses: clausesToPersist,
                    logo: finalLogo,
                    logoWidth: finalLogoWidth,
                    currentVersion: 1,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                    updatedBy: userId
                };

                const newContractRef = await addDoc(collection(db, contractsPath), newDocData);

                // Criar versão 1 na subcoleção
                const versionsColRef = collection(db, `${contractsPath}/${newContractRef.id}/versions`);
                await addDoc(versionsColRef, {
                    versionNumber: 1,
                    content: contractContent,
                    clauses: clausesToPersist,
                    notes: contractNotes || 'Criação inicial do contrato',
                    savedAt: serverTimestamp(),
                    savedBy: userId
                });
            }

            setIsContractFormOpen(false);
            setCurrentContract(null);
            setSelectedClient('');
            setSelectedTemplate('');
            setContractNameInput('');
            setContractContent('');
            setContractNotes('');
            setContractClauses([]);
        } catch (error) {
            console.error("Erro ao salvar contrato:", error);
            alert("Erro ao salvar o contrato do cliente.");
        }
    };

    const handleDeleteContract = (contract) => {
        showConfirmation({
            title: "Excluir Contrato?",
            message: `Tem certeza que deseja apagar o contrato de "${contract.clientName}"? Todas as versões também serão removidas.`,
            confirmText: "Excluir",
            confirmColor: "bg-red-600",
            showCancelButton: true,
            onConfirm: async () => {
                try {
                    // Excluir subcoleções primeiro
                    const versionsColRef = collection(db, `${contractsPath}/${contract.id}/versions`);
                    const snap = await getDocs(versionsColRef);
                    for (const dDoc of snap.docs) {
                        await deleteDoc(doc(db, `${contractsPath}/${contract.id}/versions`, dDoc.id));
                    }
                    // Excluir documento principal
                    await deleteDoc(doc(db, contractsPath, contract.id));
                    if (viewingContract && viewingContract.id === contract.id) {
                        setViewingContract(null);
                    }
                } catch (error) {
                    console.error("Erro ao excluir contrato:", error);
                }
            }
        });
    };

    const handleRestoreVersion = (version) => {
        showConfirmation({
            title: "Restaurar Versão anterior?",
            message: `Tem certeza que deseja reverter o contrato ativo para a Versão ${version.versionNumber}? Isso irá gerar uma nova versão incrementada no histórico com esse conteúdo.`,
            confirmText: "Restaurar",
            confirmColor: "bg-cyan-500",
            showCancelButton: true,
            onConfirm: async () => {
                try {
                    const nextVersionNumber = (viewingContract.currentVersion || 1) + 1;
                    const contractRef = doc(db, contractsPath, viewingContract.id);
                    
                    await updateDoc(contractRef, {
                        content: version.content,
                        currentVersion: nextVersionNumber,
                        updatedAt: serverTimestamp(),
                        updatedBy: userId
                    });

                    const versionsColRef = collection(db, `${contractsPath}/${viewingContract.id}/versions`);
                    await addDoc(versionsColRef, {
                        versionNumber: nextVersionNumber,
                        content: version.content,
                        notes: `Restaurada versão anterior (${version.versionNumber})`,
                        savedAt: serverTimestamp(),
                        savedBy: userId
                    });

                    setViewingContract(prev => ({
                        ...prev,
                        content: version.content,
                        currentVersion: nextVersionNumber
                    }));
                } catch (error) {
                    console.error("Erro ao restaurar versão:", error);
                }
            }
        });
    };

    const handleDeleteVersion = (version) => {
        if (!viewingContract) return;

        // A versão mais recente é a primeira da lista (ordenada decrescente por versionNumber)
        const latestVersionNumber = versions.length > 0 ? Math.max(...versions.map(v => v.versionNumber || 0)) : (viewingContract.currentVersion || 1);
        const isLatest = version.versionNumber === latestVersionNumber;

        if (isLatest) {
            alert("A última versão criada (mais recente) não pode ser excluída para garantir a vigência do contrato. Você pode excluir versões anteriores do histórico.");
            return;
        }

        if (versions.length <= 1) {
            alert("Não é possível excluir a única versão existente. Se desejar remover o contrato por completo, utilize a opção de exclusão do contrato.");
            return;
        }

        const isDeletingActive = version.versionNumber === viewingContract.currentVersion;
        const confirmMsg = `Tem certeza que deseja excluir a Versão ${version.versionNumber} do histórico deste contrato? Esta ação é irreversível.`;

        showConfirmation({
            title: `Excluir Versão ${version.versionNumber}?`,
            message: confirmMsg,
            confirmText: "Excluir Versão",
            confirmColor: "bg-red-600",
            showCancelButton: true,
            onConfirm: async () => {
                try {
                    // Excluir a versão na subcoleção
                    const versionDocRef = doc(db, `${contractsPath}/${viewingContract.id}/versions`, version.id);
                    await deleteDoc(versionDocRef);

                    // Se a versão apagada era a ativa, precisamos atualizar o documento do contrato com a versão imediatamente anterior
                    if (isDeletingActive) {
                        const remainingVersions = versions.filter(v => v.id !== version.id);
                        if (remainingVersions.length > 0) {
                            // A lista versions já é ordenada decrescente por versionNumber
                            const newActive = remainingVersions[0];
                            const contractRef = doc(db, contractsPath, viewingContract.id);
                            
                            await updateDoc(contractRef, {
                                currentVersion: newActive.versionNumber,
                                content: newActive.content,
                                updatedAt: serverTimestamp(),
                                updatedBy: userId
                            });

                            setViewingContract(prev => ({
                                ...prev,
                                currentVersion: newActive.versionNumber,
                                content: newActive.content
                            }));
                            setSelectedVersion(newActive);
                        }
                    } else if (selectedVersion?.id === version.id) {
                        // Se a versão selecionada para visualização foi a excluída, seleciona outra
                        const remaining = versions.filter(v => v.id !== version.id);
                        setSelectedVersion(remaining[0] || null);
                    }
                } catch (error) {
                    console.error("Erro ao excluir versão:", error);
                    alert("Erro ao excluir versão: " + error.message);
                }
            }
        });
    };

    // --- ASSINATURA ELETRÔNICA (ARCUS) ---
    const handleOpenArcusSignature = (contract, version = null) => {
        if (!isAuthorizedArcusSigner) {
            alert(`Apenas os usuários autorizados da Arcus (${ARCUS_SIGNERS.join(', ')}) podem assinar contratos.`);
            return;
        }
        setSigningContractTarget(contract);
        setSigningVersionTarget(version || selectedVersion || { versionNumber: contract.currentVersion || 1, content: contract.content });
        setIsSignatureModalOpen(true);
    };

    const handleConfirmArcusSignature = async (sigData) => {
        if (!signingContractTarget) return;

        setIsSubmittingSignature(true);
        try {
            // Obter IP público do assinante para a trilha de auditoria
            let publicIp = 'IP não identificado';
            try {
                const ipRes = await fetch('https://api.ipify.org?format=json', { signal: AbortSignal.timeout(3000) });
                if (ipRes.ok) {
                    const ipJson = await ipRes.json();
                    publicIp = ipJson.ip || publicIp;
                }
            } catch (e) {
                console.warn("Não foi possível obter IP público:", e);
            }

            const targetVersionNum = signingVersionTarget?.versionNumber || signingContractTarget.currentVersion || 1;
            const fullSignatureObj = {
                signatureBase64: sigData.signatureBase64,
                signerName: sigData.signerName,
                signerDocument: sigData.signerDocument,
                signerEmail: userEmail,
                signedAt: new Date().toISOString(),
                signerRole: 'arcus',
                ipAddress: publicIp,
                userAgent: navigator.userAgent
            };

            const updatedSignatures = {
                ...(signingContractTarget.signatures || {}),
                arcus: fullSignatureObj
            };

            // Recompilar o HTML do contrato com o carimbo de assinatura oficial
            const clausesToUse = signingVersionTarget?.clauses || signingContractTarget.clauses || [];
            let updatedContent = signingContractTarget.content;
            if (clausesToUse.length > 0) {
                const rawWithSignatures = compileClausesToHtml(
                    clausesToUse,
                    signingContractTarget.contractName || signingContractTarget.templateName,
                    signingContractTarget.logo,
                    signingContractTarget.logoWidth,
                    updatedSignatures
                );
                const matchedClient = clients.find(cl => cl.id === signingContractTarget.clientId);
                updatedContent = compileContractContent(
                    rawWithSignatures,
                    matchedClient,
                    contractValMaquinas,
                    contractValMensal,
                    ourCompanyName,
                    ourCompanyCnpj,
                    ourCompanyAddress
                );
            }

            // Atualizar no documento principal do contrato
            const contractDocRef = doc(db, contractsPath, signingContractTarget.id);
            await updateDoc(contractDocRef, {
                signatures: updatedSignatures,
                content: updatedContent,
                updatedAt: serverTimestamp(),
                updatedBy: userId
            });

            // Atualizar também na versão correspondente se existir
            if (signingVersionTarget?.id) {
                const verRef = doc(db, `${contractsPath}/${signingContractTarget.id}/versions`, signingVersionTarget.id);
                await updateDoc(verRef, {
                    signatures: updatedSignatures,
                    content: updatedContent
                }).catch(() => {});
            }

            // Atualizar estado em memória
            setViewingContract(prev => prev && prev.id === signingContractTarget.id ? {
                ...prev,
                signatures: updatedSignatures,
                content: updatedContent
            } : prev);

            setSelectedVersion(prev => prev ? {
                ...prev,
                signatures: updatedSignatures,
                content: updatedContent
            } : prev);

            setIsSignatureModalOpen(false);
            setSigningContractTarget(null);
            setSigningVersionTarget(null);
            alert("✓ Contrato assinado eletronicamente com sucesso pela Arcus!");
        } catch (error) {
            console.error("Erro ao assinar contrato:", error);
            alert("Erro ao gravar assinatura: " + error.message);
        } finally {
            setIsSubmittingSignature(false);
        }
    };

    // Filtro de pesquisa, Tipo de Cliente, Cliente Específico e Tipo/Modelo de Contrato
    const filteredContracts = useMemo(() => {
        return contracts.filter(c => {
            const matchesSearch = (c.clientName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  (c.contractName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                                  (c.templateName || '').toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesType = clientTypeFilter === 'all' || 
                (clientTypeFilter === 'Contrato' && c.clientType !== 'Avulso') || 
                (clientTypeFilter === 'Avulso' && c.clientType === 'Avulso');

            const matchesClient = selectedClientFilter === 'all' || String(c.clientId) === String(selectedClientFilter);

            const matchesContractType = selectedContractTypeFilter === 'all' || 
                c.templateId === selectedContractTypeFilter || 
                (c.contractName && c.contractName.toLowerCase() === selectedContractTypeFilter.toLowerCase()) ||
                (c.templateName && c.templateName.toLowerCase() === selectedContractTypeFilter.toLowerCase());

            const isArcusSigned = !!c.signatures?.arcus?.signedAt;
            const isClientSigned = !!c.signatures?.client?.signedAt;
            const isCompleted = isArcusSigned && isClientSigned;

            const matchesStatus = signatureStatusFilter === 'all' ||
                (signatureStatusFilter === 'completed' && isCompleted) ||
                (signatureStatusFilter === 'pending' && !isCompleted) ||
                (signatureStatusFilter === 'pending_arcus' && !isArcusSigned) ||
                (signatureStatusFilter === 'pending_client' && !isClientSigned);

            return matchesSearch && matchesType && matchesClient && matchesContractType && matchesStatus;
        });
    }, [contracts, searchQuery, clientTypeFilter, selectedClientFilter, selectedContractTypeFilter, signatureStatusFilter]);

    const filteredTemplates = useMemo(() => {
        return templates.filter(t => t.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [templates, searchQuery]);

    const canEdit = permissionLevel === 'edit';
    const canCreateTemplate = canEdit && uiItems['create-template'] !== false;
    const canCreateContract = canEdit && uiItems['create-contract'] !== false;
    const canEditContract = canEdit && uiItems['edit-contract'] !== false;
    const canDeleteContract = canEdit && uiItems['delete-contract'] !== false;

    return (
        <div className="pt-8 md:pt-12 px-4 sm:px-6 md:px-8 pb-8 text-slate-100 bg-slate-900 min-h-screen">
            <ConfirmationModal />

            {/* Cabeçalho */}
            <header className="mb-8 flex flex-col md:flex-row justify-between items-start gap-4">
                <div>
                    <h1 className="text-4xl sm:text-5xl font-black text-cyan-400 uppercase tracking-tight">Gestão de Contratos</h1>
                    <p className="text-slate-400 mt-2">Crie modelos de contratos padrões, gerencie contratos de clientes e acompanhe o histórico de alterações com versionamento completo.</p>
                </div>
            </header>

            {/* Abas e Filtro */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-6 pb-4 border-b border-slate-800">
                <div className="flex gap-2 w-full sm:w-auto">
                    <button 
                        onClick={() => { setActiveTab('contracts'); setViewingContract(null); }} 
                        className={`flex-1 sm:flex-initial px-6 py-3 rounded-2xl font-bold uppercase tracking-wider text-sm transition-all duration-300 ${activeTab === 'contracts' ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                    >
                        Contratos de Clientes
                    </button>
                    <button 
                        onClick={() => { setActiveTab('templates'); setViewingContract(null); }} 
                        className={`flex-1 sm:flex-initial px-6 py-3 rounded-2xl font-bold uppercase tracking-wider text-sm transition-all duration-300 ${activeTab === 'templates' ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/20' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                    >
                        Modelos Padrões
                    </button>
                </div>

                <div className="flex items-center gap-4 w-full sm:w-auto">
                    <input 
                        type="text" 
                        placeholder={activeTab === 'contracts' ? "Pesquisar contratos..." : "Pesquisar modelos..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full sm:w-64 bg-slate-800 border border-slate-700 rounded-full py-2.5 px-5 text-sm focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                    />
                    {((activeTab === 'contracts' && canCreateContract) || (activeTab === 'templates' && canCreateTemplate)) && (
                        <div className="flex gap-2">
                            {activeTab === 'templates' && (
                                <button
                                    onClick={handleSeedDefaultTemplate}
                                    disabled={isSeedingTemplate}
                                    className="bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-cyan-500/30 font-bold py-2.5 px-4 rounded-full transition-all uppercase tracking-wider text-xs shadow-md flex items-center gap-1.5 shrink-0"
                                    title="Criar automaticamente o modelo oficial de Prestação de Serviços de TI da Arcus com todas as 22 cláusulas"
                                >
                                    <svg className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                                    {isSeedingTemplate ? 'Gerando Modelo...' : 'Gerar Modelo Padrão TI'}
                                </button>
                            )}
                            <button
                                onClick={() => activeTab === 'contracts' ? handleOpenContractForm() : handleOpenTemplateForm()}
                                className="bg-cyan-500 hover:bg-cyan-600 text-white font-black py-2.5 px-6 rounded-full transition-all uppercase tracking-wider text-sm shadow-md flex items-center gap-2 shrink-0"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 4v16m8-8H4"/></svg>
                                Adicionar
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ABA DE CONTRATOS */}
            {activeTab === 'contracts' && (
                <div className="space-y-6">
                    {/* Barra de Filtros Avançados: Tipo de Contrato (Modelo), Cliente e Modalidade */}
                    <div className="bg-slate-800/80 border border-slate-700/80 rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between shadow-lg">
                        <div className="flex flex-wrap items-center gap-3 flex-1">
                            {/* Filtro por Cliente */}
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                    Filtrar por Cliente
                                </label>
                                <select
                                    value={selectedClientFilter}
                                    onChange={(e) => setSelectedClientFilter(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-500 cursor-pointer"
                                >
                                    <option value="all">Todos os Clientes ({clients.length})</option>
                                    {clients.map(cl => (
                                        <option key={cl.id} value={cl.id}>
                                            {cl.name} ({cl.type || 'Contrato'})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtro por Tipo de Contrato (Modelo Padrão) */}
                            <div className="flex-1 min-w-[200px]">
                                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                    Tipo / Modelo de Contrato
                                </label>
                                <select
                                    value={selectedContractTypeFilter}
                                    onChange={(e) => setSelectedContractTypeFilter(e.target.value)}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-cyan-500 cursor-pointer"
                                >
                                    <option value="all">Todos os Tipos de Contrato ({templates.length})</option>
                                    {templates.map(tpl => (
                                        <option key={tpl.id} value={tpl.id}>
                                            📄 {tpl.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtro por Modalidade (Contrato Mensal vs Avulso) */}
                            <div className="w-full sm:w-auto">
                                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                    Modalidade
                                </label>
                                <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-700">
                                    <button
                                        type="button"
                                        onClick={() => setClientTypeFilter('all')}
                                        className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${clientTypeFilter === 'all' ? 'bg-cyan-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        Todos
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setClientTypeFilter('Contrato')}
                                        className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${clientTypeFilter === 'Contrato' ? 'bg-cyan-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        Contrato
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setClientTypeFilter('Avulso')}
                                        className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${clientTypeFilter === 'Avulso' ? 'bg-cyan-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        Avulso
                                    </button>
                                </div>
                            </div>
                            {/* Filtro por Status de Assinatura */}
                            <div className="w-full sm:w-auto">
                                <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                    Assinaturas
                                </label>
                                <div className="flex bg-slate-900 p-1 rounded-xl border border-slate-700">
                                    <button
                                        type="button"
                                        onClick={() => setSignatureStatusFilter('all')}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all ${signatureStatusFilter === 'all' ? 'bg-cyan-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                    >
                                        Todos
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSignatureStatusFilter('completed')}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all ${signatureStatusFilter === 'completed' ? 'bg-emerald-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        title="Contratos 100% assinados por ambas as partes"
                                    >
                                        ✓ Vigentes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSignatureStatusFilter('pending_arcus')}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all ${signatureStatusFilter === 'pending_arcus' ? 'bg-amber-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        title="Contratos aguardando assinatura da Arcus"
                                    >
                                        Pend. Arcus
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSignatureStatusFilter('pending_client')}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all ${signatureStatusFilter === 'pending_client' ? 'bg-indigo-500 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        title="Contratos aguardando assinatura do cliente"
                                    >
                                        Pend. Cliente
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Botão para limpar filtros ativos */}
                        {(selectedClientFilter !== 'all' || selectedContractTypeFilter !== 'all' || clientTypeFilter !== 'all' || signatureStatusFilter !== 'all' || searchQuery) && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSelectedClientFilter('all');
                                    setSelectedContractTypeFilter('all');
                                    setClientTypeFilter('all');
                                    setSignatureStatusFilter('all');
                                    setSearchQuery('');
                                }}
                                className="self-end md:self-center px-3 py-2 text-xs font-bold text-cyan-400 hover:text-cyan-300 hover:bg-slate-700/50 rounded-xl border border-cyan-500/30 transition-all flex items-center gap-1.5 shrink-0"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                                Limpar Filtros
                            </button>
                        )}
                    </div>

                    {/* GRID DE MINIATURAS A4 DE CONTRATOS */}
                    <div className="space-y-4">
                        {loadingContracts ? (
                            <p className="text-slate-400">Carregando contratos...</p>
                        ) : filteredContracts.length === 0 ? (
                            <div className="p-8 text-center bg-slate-800/40 border border-slate-800 rounded-3xl">
                                <p className="text-slate-400 font-bold mb-1">Nenhum contrato encontrado com os filtros selecionados.</p>
                                <p className="text-xs text-slate-500">Tente ajustar ou limpar os filtros de cliente e tipo de contrato.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                                {filteredContracts.map(contract => {
                                    const isViewing = viewingContract?.id === contract.id;
                                    const linkedTemplate = templates.find(t => t.id === contract.templateId);
                                    const contractClauses = contract.clauses || linkedTemplate?.clauses || [];
                                    const firstClause = contractClauses.length > 0 ? contractClauses[0] : null;
                                    const firstGroup = firstClause?.group || (contractClauses.length > 0 ? 'DO OBJETO DO CONTRATO' : '');

                                    return (
                                        <div 
                                            key={contract.id}
                                            onClick={() => setViewingContract(contract)}
                                            className={`group/card bg-slate-900 border rounded-2xl p-3 flex flex-col justify-between transition-all duration-300 shadow-lg cursor-pointer ${isViewing ? 'border-cyan-500 shadow-[0_8px_30px_-8px_rgba(6,182,212,0.4)] ring-2 ring-cyan-500/30' : 'border-slate-750 hover:border-cyan-500/60 hover:shadow-[0_8px_30px_-8px_rgba(6,182,212,0.25)]'}`}
                                        >
                                            {/* Top Bar da Miniatura */}
                                            <div className="flex items-center justify-between mb-2 px-0.5">
                                                <div className="flex items-center gap-1.5 min-w-0 pr-1">
                                                    <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span>
                                                    <h3 className="text-[11px] font-black text-white uppercase tracking-tight truncate" title={contract.clientName}>
                                                        {contract.clientName}
                                                    </h3>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {(() => {
                                                        const isArcus = !!contract.signatures?.arcus?.signedAt;
                                                        const isCli = !!contract.signatures?.client?.signedAt;
                                                        if (isArcus && isCli) {
                                                            return (
                                                                <span className="text-[8.5px] font-black bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.2 rounded-full uppercase" title="Contrato assinado pela Arcus e pelo Cliente">
                                                                    ✓ Vigente
                                                                </span>
                                                            );
                                                        }
                                                        if (isArcus) {
                                                            return (
                                                                <span className="text-[8.5px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.2 rounded-full uppercase" title="Aguardando assinatura do cliente">
                                                                    Pend. Cliente
                                                                </span>
                                                            );
                                                        }
                                                        if (isCli) {
                                                            return (
                                                                <span className="text-[8.5px] font-bold bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-1.5 py-0.2 rounded-full uppercase" title="Aguardando assinatura da Arcus">
                                                                    Pend. Arcus
                                                                </span>
                                                            );
                                                        }
                                                        return (
                                                            <span className="text-[8.5px] font-bold bg-slate-800 text-slate-400 border border-slate-700 px-1.5 py-0.2 rounded-full uppercase" title="Aguardando assinaturas">
                                                                Não assinado
                                                            </span>
                                                        );
                                                    })()}
                                                    <span className="text-[9px] font-mono text-cyan-400 bg-cyan-950/70 border border-cyan-800/40 px-1.5 py-0.2 rounded shrink-0">
                                                        V{contract.currentVersion || 1}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Miniatura A4 Proporcional e Compacta (Página 1 / Capa do Contrato) */}
                                            <div 
                                                className="relative w-full aspect-[1/1.38] bg-slate-950/70 rounded-xl p-2.5 flex items-center justify-center overflow-hidden border border-slate-800 group-hover/card:border-cyan-500/40 transition-colors"
                                                title="Clique para visualizar os detalhes e histórico deste contrato"
                                            >
                                                {/* Folha de Papel A4 Branca com Conteúdo Real */}
                                                <div className="w-full h-full bg-white text-slate-900 rounded shadow-md p-3 flex flex-col justify-between overflow-hidden select-none border border-slate-250 relative group-hover/card:scale-[1.02] transition-transform duration-200">
                                                    
                                                    <div className="space-y-2">
                                                        {/* Cabeçalho Oficial: Logotipo + Título Centralizado */}
                                                        <div className="border-b border-slate-800/30 pb-1.5">
                                                            {contract.logo || linkedTemplate?.logo ? (
                                                                <div className="flex justify-start mb-1">
                                                                    <img 
                                                                        src={contract.logo || linkedTemplate?.logo} 
                                                                        alt="Logo" 
                                                                        className="h-4 sm:h-5 object-contain"
                                                                        style={{ maxWidth: '85px' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-1 mb-1 text-slate-800">
                                                                    <div className="w-3 h-3 rounded bg-cyan-600 flex items-center justify-center text-[7px] text-white font-black">A</div>
                                                                    <span className="text-[7px] font-black tracking-wider uppercase text-slate-700">Arcus Tecnologia</span>
                                                                </div>
                                                            )}
                                                            <h4 className="text-[7.5px] font-black text-slate-950 uppercase text-center leading-tight tracking-tight line-clamp-2">
                                                                {contract.contractName || contract.templateName || 'Contrato de Prestação de Serviços de TI'}
                                                            </h4>
                                                        </div>

                                                        {/* Qualificação Real das Partes */}
                                                        <div className="text-[6.5px] text-slate-600 leading-tight space-y-0.5 text-justify">
                                                            <p className="line-clamp-2">
                                                                <strong>CONTRATANTE:</strong> {contract.clientName || 'Cliente'}.
                                                            </p>
                                                            <p className="line-clamp-2">
                                                                <strong>CONTRATADA:</strong> ARCUS IT NETWORK SOLUTIONS TI LTDA - ME.
                                                            </p>
                                                        </div>

                                                        {/* Cláusula(s) Reais do Contrato */}
                                                        {firstClause ? (
                                                            <div className="border-t border-slate-200/80 pt-1.5 space-y-1">
                                                                {firstGroup && (
                                                                    <div className="bg-slate-100 rounded px-1 py-0.2 text-[6px] font-black uppercase text-slate-700 tracking-wider line-clamp-1">
                                                                        📁 {firstGroup}
                                                                    </div>
                                                                )}
                                                                <p className="text-[6.5px] text-slate-700 line-clamp-4 leading-relaxed text-justify">
                                                                    <strong className="text-slate-900 font-black">
                                                                        {firstClause.title && !/^cl[áa]usula\s*\d+[ªº°]?/i.test(firstClause.title.trim()) 
                                                                            ? `Cláusula 1ª - ${firstClause.title}: ` 
                                                                            : 'Cláusula 1ª: '}
                                                                    </strong>
                                                                    {firstClause.text}
                                                                </p>
                                                            </div>
                                                        ) : contract.content ? (
                                                            <div className="border-t border-slate-200/80 pt-1">
                                                                <div 
                                                                    className="text-[6.5px] text-slate-600 line-clamp-5 leading-tight"
                                                                    dangerouslySetInnerHTML={{ 
                                                                        __html: contract.content.replace(/<h1[^>]*>.*?<\/h1>/gi, '').replace(/<img[^>]*>/gi, '') 
                                                                    }}
                                                                />
                                                            </div>
                                                        ) : null}
                                                    </div>

                                                    {/* Rodapé da Folha A4 */}
                                                    <div className="pt-1 border-t border-slate-200 flex justify-between items-center text-[6px] text-slate-400 font-mono">
                                                        <span>Doc. Oficial Arcus</span>
                                                        <span>Pág. 1</span>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Barra Inferior com Identificador e Ações Compactas */}
                                            <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-800 text-[10px] text-slate-400">
                                                <span className="truncate max-w-[90px] text-cyan-400/90 font-medium" title={contract.contractName || contract.templateName}>
                                                    {contract.contractName || contract.templateName || 'Contrato TI'}
                                                </span>
                                                <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                                    {canEditContract && (
                                                        <button 
                                                            onClick={() => handleOpenContractForm(contract)}
                                                            className="px-2 py-1 bg-slate-800 hover:bg-cyan-500/20 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/50 rounded-lg font-bold transition-all flex items-center gap-1 text-[10px] shadow-sm"
                                                            title="Editar / Gerar Nova Versão"
                                                        >
                                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                            Editar
                                                        </button>
                                                    )}
                                                    {canDeleteContract && (
                                                        <button 
                                                            onClick={() => handleDeleteContract(contract)}
                                                            className="p-1 hover:bg-red-500/10 rounded-lg text-slate-500 hover:text-red-400 transition-colors"
                                                            title="Excluir Contrato"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MODAL DE VISUALIZAÇÃO DO CONTRATO SELECIONADO & HISTÓRICO DE VERSÕES */}
            {viewingContract && (
                <div className="fixed inset-0 bg-slate-950/85 backdrop-blur-md flex justify-center items-center z-[150] p-4 animate-fadeIn">
                    <div className="bg-slate-900 rounded-3xl border border-slate-700/80 p-6 sm:p-8 shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto custom-scrollbar flex flex-col animate-scaleIn">
                        <div className="flex justify-between items-start flex-wrap gap-4 mb-6 pb-5 border-b border-slate-800">
                            <div>
                                <div className="flex items-center gap-3 mb-1 flex-wrap">
                                    <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">{viewingContract.clientName}</h2>
                                    <span className="bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 text-xs font-black px-3 py-1 rounded-full uppercase tracking-wider">
                                        V{viewingContract.currentVersion || 1} Ativa
                                    </span>
                                    <span className="bg-slate-950 text-slate-300 border border-slate-800 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5">
                                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span>
                                        <span>{viewingContract.contractName || viewingContract.templateName || 'Contrato de Suporte'}</span>
                                    </span>
                                </div>
                                <p className="text-xs text-slate-400">
                                    Nome para abertura de chamado: <strong className="text-cyan-400">{viewingContract.contractName || viewingContract.templateName || 'Contrato de Suporte'}</strong>
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                {/* Botão de Assinatura Eletrônica da Arcus */}
                                {(() => {
                                    const isArcusSigned = !!viewingContract.signatures?.arcus?.signedAt;
                                    const arcusSig = viewingContract.signatures?.arcus;

                                    if (isArcusSigned) {
                                        return (
                                            <div className="flex items-center gap-1.5 bg-emerald-950/70 border border-emerald-500/50 px-3.5 py-1.5 rounded-2xl text-xs font-bold text-emerald-400 shadow-sm" title={`Assinado por ${arcusSig.signerName} (${arcusSig.signerEmail}) em ${new Date(arcusSig.signedAt).toLocaleString('pt-BR')}`}>
                                                <span>✓ Assinado pela Arcus</span>
                                            </div>
                                        );
                                    }

                                    if (isAuthorizedArcusSigner) {
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => handleOpenArcusSignature(viewingContract, selectedVersion)}
                                                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-black py-2 px-5 rounded-2xl transition-all uppercase tracking-wider text-xs shadow-lg shadow-cyan-500/30 flex items-center gap-2 animate-pulse"
                                                title="Assinar este contrato eletronicamente em nome da Arcus"
                                            >
                                                <span>✍️ Assinar pela Arcus</span>
                                            </button>
                                        );
                                    }

                                    return (
                                        <div className="flex items-center gap-1.5 bg-amber-950/60 border border-amber-500/40 px-3 py-1.5 rounded-2xl text-[11px] font-bold text-amber-400" title="Apenas jonathan@ e juliano@ podem assinar">
                                            <span>⏳ Pendente Assinatura Arcus</span>
                                        </div>
                                    );
                                })()}

                                {canEditContract && (
                                    <button 
                                        onClick={() => handleOpenContractForm(viewingContract)}
                                        className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold py-2 px-5 rounded-2xl transition-all uppercase tracking-wider text-xs shadow-lg shadow-cyan-500/20 flex items-center gap-2"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                                        Nova Versão
                                    </button>
                                )}
                                <button 
                                    onClick={() => setViewingContract(null)}
                                    className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 p-2.5 rounded-2xl transition-colors"
                                    title="Fechar Modal"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 flex-1">
                            {/* Coluna Esquerda: Histórico de Versões */}
                            <div className="md:col-span-1 border-r border-slate-800/80 pr-0 md:pr-6 space-y-3">
                                <h3 className="text-xs font-black uppercase tracking-wider text-slate-400">Histórico de Versões</h3>
                                {loadingVersions ? (
                                    <p className="text-slate-500 text-xs italic">Carregando versões...</p>
                                ) : versions.length === 0 ? (
                                    <p className="text-slate-500 text-xs italic">Nenhuma versão arquivada.</p>
                                ) : (
                                    <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1 custom-scrollbar">
                                         {versions.map(v => {
                                             const isSelected = selectedVersion?.id === v.id;
                                             const isActive = v.versionNumber === viewingContract.currentVersion;
                                             const isLatest = versions.length > 0 && v.versionNumber === Math.max(...versions.map(item => item.versionNumber || 0));
                                             return (
                                                 <div 
                                                     key={v.id}
                                                     onClick={() => setSelectedVersion(v)}
                                                     className={`group/ver p-3 rounded-2xl border transition-all cursor-pointer relative ${isSelected ? 'bg-cyan-950 border-cyan-500 text-white shadow-md' : 'bg-slate-950/60 border-slate-800 text-slate-300 hover:border-slate-700'}`}
                                                 >
                                                     <div className="flex justify-between items-center mb-1">
                                                         <div className="flex items-center gap-1.5">
                                                             <span className="font-bold text-xs uppercase">Versão {v.versionNumber}</span>
                                                             {isActive && (
                                                                 <span className="text-[9px] bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-black px-1.5 py-0.2 rounded-full uppercase">
                                                                     Ativa
                                                                 </span>
                                                             )}
                                                         </div>
                                                         <div className="flex items-center gap-1">
                                                             <span className="text-[10px] text-slate-500">{v.savedAt?.seconds ? new Date(v.savedAt.seconds * 1000).toLocaleDateString() : 'N/A'}</span>
                                                             {canDeleteContract && !isLatest && versions.length > 1 && (
                                                                 <button
                                                                     type="button"
                                                                     onClick={(e) => {
                                                                         e.stopPropagation();
                                                                         handleDeleteVersion(v);
                                                                     }}
                                                                     className="opacity-0 group-hover/ver:opacity-100 hover:opacity-100 p-1 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-all ml-1"
                                                                     title={`Excluir Versão ${v.versionNumber}`}
                                                                 >
                                                                     <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                                     </svg>
                                                                 </button>
                                                             )}
                                                         </div>
                                                     </div>
                                                     <p className="text-xs text-slate-400 line-clamp-1 italic">{v.notes || 'Sem anotações'}</p>
                                                 </div>
                                             );
                                         })}
                                     </div>
                                )}
                            </div>

                            {/* Coluna Direita: Visualizador de Conteúdo da Versão Selecionada */}
                            <div className="md:col-span-2 space-y-4">
                                {selectedVersion ? (
                                    <>
                                        <div className="flex justify-between items-center flex-wrap gap-2 bg-slate-950/70 p-3.5 rounded-2xl border border-slate-800">
                                            <div>
                                                <span className="text-xs font-black text-slate-500 uppercase tracking-widest block">Exibindo Conteúdo</span>
                                                <span className="text-sm font-bold text-white">Versão {selectedVersion.versionNumber} {selectedVersion.versionNumber === viewingContract.currentVersion && '(Ativa)'}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {canEditContract && selectedVersion.versionNumber !== viewingContract.currentVersion && (
                                                    <button 
                                                        onClick={() => handleRestoreVersion(selectedVersion)}
                                                        className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold py-1.5 px-4 rounded-xl text-xs uppercase tracking-wider transition-colors shadow-md"
                                                    >
                                                        Restaurar Esta Versão
                                                    </button>
                                                )}
                                                {canDeleteContract && selectedVersion.versionNumber !== Math.max(...versions.map(item => item.versionNumber || 0)) && versions.length > 1 && (
                                                    <button 
                                                        onClick={() => handleDeleteVersion(selectedVersion)}
                                                        className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 hover:border-red-500/50 font-bold py-1.5 px-3.5 rounded-xl text-xs uppercase tracking-wider transition-colors flex items-center gap-1.5"
                                                        title={`Excluir Versão ${selectedVersion.versionNumber}`}
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                                        </svg>
                                                        Excluir Versão
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Status das Assinaturas Eletrônicas */}
                                        {(() => {
                                            const sigs = viewingContract.signatures || {};
                                            const arcus = sigs.arcus;
                                            const client = sigs.client;
                                            const isComplete = arcus?.signedAt && client?.signedAt;

                                            return (
                                                <div className={`p-4 rounded-2xl border transition-all ${isComplete ? 'bg-emerald-950/40 border-emerald-500/40' : 'bg-slate-950/70 border-slate-800'}`}>
                                                    <div className="flex justify-between items-center mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-black uppercase tracking-wider text-slate-300">
                                                                Status das Assinaturas Digitais
                                                            </span>
                                                            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full uppercase ${isComplete ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'}`}>
                                                                {isComplete ? '✓ Contrato Vigente (100% Assinado)' : 'Aguardando Assinatura(s)'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                                        {/* CONTRATADA (Arcus) */}
                                                        <div className={`p-3 rounded-xl border ${arcus?.signedAt ? 'bg-emerald-950/50 border-emerald-800/60 text-emerald-200' : 'bg-slate-900 border-slate-800 text-slate-400'}`}>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="font-bold uppercase text-[10px] tracking-wider text-slate-400">Contratada (Arcus)</span>
                                                                <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${arcus?.signedAt ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                                                    {arcus?.signedAt ? 'Assinado' : 'Pendente'}
                                                                </span>
                                                            </div>
                                                            {arcus?.signedAt ? (
                                                                <div className="space-y-0.5 text-[11px]">
                                                                    <p className="font-bold text-white">{arcus.signerName}</p>
                                                                    <p className="text-slate-400 text-[10px]">{arcus.signerEmail} {arcus.signerDocument && `• ${arcus.signerDocument}`}</p>
                                                                    <p className="text-slate-500 font-mono text-[9px]">{new Date(arcus.signedAt).toLocaleString('pt-BR')} • IP: {arcus.ipAddress || '—'}</p>
                                                                </div>
                                                            ) : (
                                                                <div className="flex justify-between items-center pt-1">
                                                                    <span className="text-[11px] text-slate-500 italic">Aguardando assinatura da Arcus</span>
                                                                    {isAuthorizedArcusSigner && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenArcusSignature(viewingContract, selectedVersion)}
                                                                            className="px-2.5 py-1 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-lg text-[10px] uppercase tracking-wider transition-colors shadow-sm"
                                                                        >
                                                                            Assinar Agora
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* CONTRATANTE (Cliente) */}
                                                        <div className={`p-3 rounded-xl border ${client?.signedAt ? 'bg-emerald-950/50 border-emerald-800/60 text-emerald-200' : 'bg-slate-900 border-slate-800 text-slate-400'}`}>
                                                            <div className="flex justify-between items-center mb-1">
                                                                <span className="font-bold uppercase text-[10px] tracking-wider text-slate-400">Contratante (Cliente)</span>
                                                                <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded ${client?.signedAt ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                                                                    {client?.signedAt ? 'Assinado' : 'Pendente'}
                                                                </span>
                                                            </div>
                                                            {client?.signedAt ? (
                                                                <div className="space-y-0.5 text-[11px]">
                                                                    <p className="font-bold text-white">{client.signerName}</p>
                                                                    <p className="text-slate-400 text-[10px]">{client.signerEmail} {client.signerDocument && `• ${client.signerDocument}`}</p>
                                                                    <p className="text-slate-500 font-mono text-[9px]">{new Date(client.signedAt).toLocaleString('pt-BR')} • IP: {client.ipAddress || '—'}</p>
                                                                </div>
                                                            ) : (
                                                                <div className="pt-1">
                                                                    <span className="text-[11px] text-slate-500 italic">Aguardando assinatura do cliente pelo Portal</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Box de Anotações / Resumo da Alteração desta Versão (Exatamente onde indicado pela seta) */}
                                        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 p-4 rounded-2xl border border-cyan-500/30 shadow-lg flex items-start gap-3">
                                            <div className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 shrink-0 mt-0.5">
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-[11px] font-black uppercase tracking-wider text-cyan-400">
                                                        Anotações & Resumo da Alteração Contratual
                                                    </span>
                                                    <span className="text-[9px] font-mono text-slate-400 bg-slate-800 px-1.5 py-0.2 rounded border border-slate-700">
                                                        V{selectedVersion.versionNumber}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-200 leading-relaxed font-medium">
                                                    {selectedVersion.notes?.trim() || <span className="text-slate-500 italic">Nenhuma anotação informada para esta versão contratual.</span>}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="max-h-[540px] overflow-y-auto p-4 sm:p-8 bg-slate-950 rounded-2xl border border-slate-800 flex justify-center items-start custom-scrollbar">
                                            <div className="w-full max-w-3xl bg-white text-slate-900 p-8 sm:p-12 rounded-xl shadow-xl border border-slate-200 text-xs">
                                                <div dangerouslySetInnerHTML={{ __html: selectedVersion.content || '<p class="text-slate-500 italic">Conteúdo vazio.</p>' }} />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <p className="text-slate-500 italic">Selecione uma versão do histórico para visualizar o conteúdo do contrato.</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ABA DE MODELOS */}
            {activeTab === 'templates' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5">
                    {loadingTemplates ? (
                        <p className="text-slate-400 col-span-full">Carregando modelos...</p>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="col-span-full py-12 px-6 bg-slate-800/40 border border-slate-700/60 rounded-3xl text-center space-y-4">
                            <p className="text-slate-400 font-medium">Nenhum modelo de contrato cadastrado ainda.</p>
                            {canCreateTemplate && (
                                <button
                                    onClick={handleSeedDefaultTemplate}
                                    disabled={isSeedingTemplate}
                                    className="inline-flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-2xl font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>
                                    {isSeedingTemplate ? 'Gerando Modelo...' : 'Gerar Modelo Padrão Oficial TI (Arcus)'}
                                </button>
                            )}
                        </div>
                    ) : (
                        filteredTemplates.map(template => {
                            // Obtém as cláusulas reais cadastradas no modelo ou extrai as primeiras cláusulas
                            const actualClauses = template.clauses && template.clauses.length > 0 ? template.clauses : [];
                            const firstClause = actualClauses.length > 0 ? actualClauses[0] : null;
                            const firstGroup = firstClause?.group || (actualClauses.length > 0 ? 'DO OBJETO DO CONTRATO' : '');
                            const clausesCount = actualClauses.length;

                            return (
                                <div 
                                    key={template.id}
                                    className="group/card bg-slate-900 border border-slate-750 hover:border-cyan-500/60 rounded-2xl p-3 flex flex-col justify-between transition-all duration-300 shadow-lg hover:shadow-[0_8px_30px_-8px_rgba(6,182,212,0.25)]"
                                >
                                    {/* Top Bar da Miniatura */}
                                    <div className="flex items-center justify-between mb-2 px-0.5">
                                        <div className="flex items-center gap-1.5 min-w-0 pr-1">
                                            <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0"></span>
                                            <h3 className="text-[11px] font-black text-white uppercase tracking-tight truncate" title={template.name}>
                                                {template.name}
                                            </h3>
                                        </div>
                                        <span className="text-[9px] font-mono text-cyan-400 bg-cyan-950/70 border border-cyan-800/40 px-1.5 py-0.2 rounded shrink-0">
                                            {clausesCount > 0 ? `${clausesCount} cl.` : 'Modelo'}
                                        </span>
                                    </div>

                                    {/* Miniatura A4 Proporcional e Compacta (Página 1 / Capa) */}
                                    <div 
                                        onClick={() => canCreateTemplate && handleOpenTemplateForm(template)}
                                        className="relative w-full aspect-[1/1.38] bg-slate-950/70 rounded-xl p-2.5 flex items-center justify-center overflow-hidden border border-slate-800 cursor-pointer group-hover/card:border-cyan-500/40 transition-colors"
                                        title="Clique para editar este modelo"
                                    >
                                        {/* Folha de Papel A4 Branca com Conteúdo Real */}
                                        <div className="w-full h-full bg-white text-slate-900 rounded shadow-md p-3 flex flex-col justify-between overflow-hidden select-none border border-slate-250 relative group-hover/card:scale-[1.02] transition-transform duration-200">
                                            
                                            <div className="space-y-2">
                                                {/* Cabeçalho Oficial: Logotipo + Título Centralizado */}
                                                <div className="border-b border-slate-800/30 pb-1.5">
                                                    {template.logo ? (
                                                        <div className="flex justify-start mb-1">
                                                            <img 
                                                                src={template.logo} 
                                                                alt="Logo" 
                                                                className="h-4 sm:h-5 object-contain"
                                                                style={{ maxWidth: '85px' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="flex items-center gap-1 mb-1 text-slate-800">
                                                            <div className="w-3 h-3 rounded bg-cyan-600 flex items-center justify-center text-[7px] text-white font-black">A</div>
                                                            <span className="text-[7px] font-black tracking-wider uppercase text-slate-700">Arcus Tecnologia</span>
                                                        </div>
                                                    )}
                                                    <h4 className="text-[7.5px] font-black text-slate-950 uppercase text-center leading-tight tracking-tight line-clamp-2">
                                                        {template.name}
                                                    </h4>
                                                </div>

                                                {/* Qualificação Real das Partes */}
                                                <div className="text-[6.5px] text-slate-600 leading-tight space-y-0.5 text-justify">
                                                    <p className="line-clamp-2">
                                                        <strong>CONTRATANTE:</strong> {"{{NOME_CONTRATANTE}}"}, CNPJ/CPF {"{{CNPJ_CONTRATANTE}}"}.
                                                    </p>
                                                    <p className="line-clamp-2">
                                                        <strong>CONTRATADA:</strong> ARCUS IT NETWORK SOLUTIONS TI LTDA - ME.
                                                    </p>
                                                </div>

                                                {/* Cláusula(s) Reais do Modelo */}
                                                {firstClause ? (
                                                    <div className="border-t border-slate-200/80 pt-1.5 space-y-1">
                                                        {firstGroup && (
                                                            <div className="bg-slate-100 rounded px-1 py-0.2 text-[6px] font-black uppercase text-slate-700 tracking-wider line-clamp-1">
                                                                📁 {firstGroup}
                                                            </div>
                                                        )}
                                                        <p className="text-[6.5px] text-slate-700 line-clamp-4 leading-relaxed text-justify">
                                                            <strong className="text-slate-900 font-black">
                                                                {firstClause.title && !/^cl[áa]usula\s*\d+[ªº°]?/i.test(firstClause.title.trim()) 
                                                                    ? `Cláusula 1ª - ${firstClause.title}: ` 
                                                                    : 'Cláusula 1ª: '}
                                                            </strong>
                                                            {firstClause.text}
                                                        </p>
                                                    </div>
                                                ) : template.content ? (
                                                    <div className="border-t border-slate-200/80 pt-1">
                                                        <div 
                                                            className="text-[6.5px] text-slate-600 line-clamp-5 leading-tight"
                                                            dangerouslySetInnerHTML={{ 
                                                                __html: template.content.replace(/<h1[^>]*>.*?<\/h1>/gi, '').replace(/<img[^>]*>/gi, '') 
                                                            }}
                                                        />
                                                    </div>
                                                ) : null}
                                            </div>

                                            {/* Rodapé da Folha A4 */}
                                            <div className="pt-1 border-t border-slate-200 flex justify-between items-center text-[6px] text-slate-400 font-mono">
                                                <span>Doc. Oficial Arcus</span>
                                                <span>Pág. 1</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Barra Inferior com Data e Ações Compactas */}
                                    <div className="flex justify-between items-center pt-2 mt-2 border-t border-slate-800 text-[10px] text-slate-400">
                                        <span className="truncate">
                                            {template.updatedAt?.seconds ? new Date(template.updatedAt.seconds * 1000).toLocaleDateString('pt-BR') : '25/09/2026'}
                                        </span>
                                        {canEdit && (
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                {canCreateTemplate && (
                                                    <button 
                                                        onClick={() => handleOpenTemplateForm(template)}
                                                        className="px-2 py-1 bg-slate-800 hover:bg-cyan-500/20 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/50 rounded-lg font-bold transition-all flex items-center gap-1 text-[10px] shadow-sm"
                                                        title="Editar Modelo e Cláusulas"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                        Editar
                                                    </button>
                                                )}
                                                {canDeleteContract && (
                                                    <button 
                                                        onClick={() => handleDeleteTemplate(template)}
                                                        className="p-1 hover:bg-red-500/10 rounded-lg text-slate-500 hover:text-red-400 transition-colors"
                                                        title="Excluir Modelo"
                                                    >
                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* FORMULÁRIO DE MODELO (MODAL COM CONSTRUTOR DE CLÁUSULAS E PREVIEW LADO A LADO) */}
            {isTemplateFormOpen && (
                <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-[150] p-1 sm:p-2 md:p-3">
                    <div className="bg-slate-900 rounded-3xl border border-slate-700 shadow-2xl w-full max-w-[98vw] 2xl:max-w-[1720px] h-[96vh] flex flex-col overflow-hidden">
                        {/* Top Bar / Header */}
                        <div className="p-4 sm:p-5 border-b border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 bg-slate-900/95 shrink-0">
                            <div className="flex-1 w-full md:w-auto">
                                <div className="flex items-center gap-3">
                                    <span className="p-2 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/20">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                    </span>
                                    <div>
                                        <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
                                            {currentTemplate ? 'Editar Modelo de Contrato' : 'Novo Modelo de Contrato'}
                                        </h2>
                                        <p className="text-xs text-slate-400">Adicione e organize as cláusulas à esquerda para visualizar o documento oficial sendo montado em tempo real à direita.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2.5 w-full md:w-auto justify-end">
                                <button 
                                    type="button"
                                    onClick={handleLoadOfficialClausesIntoEditor}
                                    className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-cyan-400 border border-cyan-500/30 font-bold text-xs uppercase tracking-wider transition-all flex items-center gap-1.5"
                                    title="Preencher automaticamente as 22 cláusulas do contrato padrão Arcus neste editor"
                                >
                                    <svg className="w-3.5 h-3.5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/></svg>
                                    Carregar 22 Cláusulas Oficiais
                                </button>
                                <button 
                                    type="button"
                                    onClick={() => setIsTemplateFormOpen(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 font-bold text-xs uppercase tracking-wider transition-all"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="button"
                                    onClick={handleSaveTemplate}
                                    className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                                    Salvar Modelo
                                </button>
                            </div>
                        </div>

                        {/* Configurações do Modelo: Nome e Logotipo */}
                        <div className="px-5 py-2.5 bg-slate-950/60 border-b border-slate-800/80 grid grid-cols-1 md:grid-cols-12 gap-3 items-center shrink-0">
                            <div className="md:col-span-7 flex items-center gap-2.5">
                                <label className="text-xs font-black uppercase tracking-wider text-cyan-400 shrink-0">Nome do Modelo:</label>
                                <input 
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="Ex: Contrato de Manutenção e Suporte Técnico Mensal"
                                    className="w-full bg-slate-800/80 border border-slate-700/80 rounded-xl px-3.5 py-1.5 text-xs text-white font-medium focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder-slate-500"
                                    required
                                />
                            </div>

                            <div className="md:col-span-5 flex items-center gap-2">
                                <label className="text-xs font-black uppercase tracking-wider text-cyan-400 shrink-0">Logo:</label>
                                <div className="flex-1 flex items-center gap-2">
                                    <input 
                                        type="text"
                                        value={templateLogo}
                                        onChange={(e) => setTemplateLogo(e.target.value)}
                                        placeholder="URL da imagem ou Upload..."
                                        className="flex-1 bg-slate-800/80 border border-slate-700/80 rounded-xl px-3 py-1.5 text-xs text-white focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder-slate-500"
                                    />
                                    <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 text-cyan-400 border border-cyan-500/40 px-3 py-1.5 rounded-xl text-xs font-bold transition-all shrink-0 flex items-center gap-1.5" title="Carregar imagem do logo do seu computador">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                        <span>Anexar</span>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            className="hidden" 
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                const reader = new FileReader();
                                                reader.onload = (event) => {
                                                    setTemplateLogo(event.target.result);
                                                };
                                                reader.readAsDataURL(file);
                                            }}
                                        />
                                    </label>
                                    {templateLogo && (
                                        <button
                                            type="button"
                                            onClick={() => setTemplateLogo('')}
                                            className="text-slate-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                                            title="Remover logo"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Split-Screen Body */}
                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 min-h-0 overflow-hidden">
                            {/* COLUNA ESQUERDA: Criador & Gerenciador de Cláusulas (Ampliada para 6 colunas) */}
                            <div className="lg:col-span-6 xl:col-span-5 border-r border-slate-800 flex flex-col h-full min-h-0 overflow-hidden bg-slate-900/50">
                                {/* Formulário de Adicionar / Editar Cláusula */}
                                <div className="p-4 sm:p-4 border-b border-slate-800 bg-slate-950/40 shrink-0 space-y-2.5">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-xs font-black uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                                            <span>{editingClauseIndex !== null ? '✏️ Editar Cláusula' : '➕ Nova Cláusula do Modelo'}</span>
                                        </h3>
                                        {editingClauseIndex !== null && (
                                            <button
                                                type="button"
                                                onClick={handleCancelEditClause}
                                                className="text-[11px] font-bold text-slate-400 hover:text-slate-200 underline"
                                            >
                                                Cancelar Edição
                                            </button>
                                        )}
                                    </div>

                                    {/* Seleção Rigorosa de Seção / Grupo (Sem ambiguidade) */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-[11px] font-black text-cyan-400 uppercase tracking-wider">
                                                Grupo / Seção do Contrato
                                            </label>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setIsManageGroupsOpen(true)}
                                                    className="text-[10px] font-bold text-amber-400 hover:text-amber-300 underline flex items-center gap-1"
                                                    title="Editar ou excluir grupos criados"
                                                >
                                                    ⚙️ Gerenciar Grupos
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsCustomGroup(!isCustomGroup);
                                                        if (!isCustomGroup) setCustomGroupName('');
                                                    }}
                                                    className="text-[10px] font-bold text-cyan-400 hover:text-cyan-300 underline flex items-center gap-1"
                                                >
                                                    {isCustomGroup ? '← Escolher Grupo Existente' : '+ Criar Novo Grupo'}
                                                </button>
                                            </div>
                                        </div>

                                        {isCustomGroup ? (
                                            <div className="flex gap-2">
                                                <input 
                                                    type="text"
                                                    value={customGroupName}
                                                    onChange={(e) => setCustomGroupName(e.target.value)}
                                                    placeholder="Digite o nome do novo grupo (Ex: DAS PENALIDADES E MULTAS)"
                                                    className="w-full bg-slate-800 border-2 border-cyan-500 rounded-xl px-3.5 py-2 text-xs text-white font-bold placeholder-slate-500 focus:outline-none uppercase"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setIsCustomGroup(false)}
                                                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl"
                                                >
                                                    Voltar
                                                </button>
                                            </div>
                                        ) : (
                                            <select 
                                                value={clauseInputGroup}
                                                onChange={(e) => {
                                                    if (e.target.value === '__NEW_GROUP__') {
                                                        setIsCustomGroup(true);
                                                        setCustomGroupName('');
                                                    } else {
                                                        setClauseInputGroup(e.target.value);
                                                    }
                                                }}
                                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white font-bold focus:ring-2 focus:ring-cyan-400 focus:outline-none uppercase cursor-pointer"
                                            >
                                                <option value="">Selecione o Grupo / Seção...</option>
                                                {/* Grupos criados neste modelo */}
                                                {Array.from(new Set([...customGroups, ...modelClauses.map(c => c.group).filter(Boolean)])).map(g => (
                                                    <option key={g} value={g}>📁 {g}</option>
                                                ))}
                                                <option value="__NEW_GROUP__">+ Outro Grupo / Nova Seção...</option>
                                            </select>
                                        )}
                                    </div>

                                    {/* Tipo de Cláusula: Principal vs Complementar */}
                                    <div className="bg-slate-900/90 p-2 rounded-xl border border-slate-800">
                                        <label className="block text-[10px] font-black text-cyan-400 uppercase tracking-wider mb-1.5">
                                            Tipo de Cláusula
                                        </label>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setIsClauseComplementary(false)}
                                                className={`py-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${!isClauseComplementary ? 'bg-cyan-500 text-white shadow-md' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                                            >
                                                <span>📋 Principal</span>
                                                <span className="text-[10px] opacity-75 font-mono">(ex: Cláusula 10ª)</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setIsClauseComplementary(true)}
                                                className={`py-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${isClauseComplementary ? 'bg-amber-500 text-white shadow-md' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                                            >
                                                <span>↳ Complementar</span>
                                                <span className="text-[10px] opacity-75 font-mono">(ex: Cláusula 10.1)</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Identificação / Subtítulo Opcional da Cláusula */}
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                {isClauseComplementary ? 'Identificação Complementar' : 'Numeração & Subtítulo da Cláusula'}
                                            </label>
                                            <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800/40">
                                                {(() => {
                                                    if (editingClauseIndex !== null) {
                                                        const num = calculateClauseNumbering(modelClauses, editingClauseIndex);
                                                        return `Editando: ${num.displayTitle}`;
                                                    }
                                                    if (isClauseComplementary) {
                                                        const simulated = [...modelClauses, { isComplementary: true }];
                                                        const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                        return `Próxima: ${num.displayTitle}`;
                                                    }
                                                    const simulated = [...modelClauses, { isComplementary: false }];
                                                    const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                    return `Próxima: ${num.displayTitle}`;
                                                })()}
                                            </span>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <span className={`px-3 py-1.5 rounded-xl text-xs font-black font-mono shrink-0 border ${isClauseComplementary ? 'bg-amber-950/60 border-amber-600/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-cyan-400'}`}>
                                                {(() => {
                                                    if (editingClauseIndex !== null) {
                                                        const num = calculateClauseNumbering(modelClauses, editingClauseIndex);
                                                        return num.displayTitle;
                                                    }
                                                    if (isClauseComplementary) {
                                                        const simulated = [...modelClauses, { isComplementary: true }];
                                                        const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                        return num.displayTitle;
                                                    }
                                                    const simulated = [...modelClauses, { isComplementary: false }];
                                                    const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                    return num.displayTitle;
                                                })()}
                                            </span>
                                            <input 
                                                type="text"
                                                value={clauseInputTitle}
                                                onChange={(e) => setClauseInputTitle(e.target.value)}
                                                placeholder={isClauseComplementary ? "Subtítulo opcional da complementar" : "Subtítulo opcional (Ex: DO OBJETO)"}
                                                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-1.5 text-xs text-white font-bold placeholder-slate-500 focus:ring-2 focus:ring-cyan-400 focus:outline-none uppercase"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                            Texto da Cláusula
                                        </label>
                                        <textarea 
                                            rows="3"
                                            value={clauseInputText}
                                            onChange={(e) => setClauseInputText(e.target.value)}
                                            placeholder="Digite o texto da cláusula... Você pode usar as tags dinâmicas abaixo que serão preenchidas ao vincular o contrato a um cliente."
                                            className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-cyan-400 focus:outline-none resize-none leading-relaxed"
                                        />
                                    </div>

                                    {/* Atalhos para Variáveis Dinâmicas */}
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">Variáveis:</span>
                                        {[
                                            { label: 'Cliente', tag: '{{NOME_CONTRATANTE}}' },
                                            { label: 'CNPJ', tag: '{{CNPJ_CONTRATANTE}}' },
                                            { label: 'Endereço', tag: '{{ENDERECO_CONTRATANTE}}' },
                                            { label: 'Máquinas', tag: '{{QTD_MAQUINAS}}' },
                                            { label: 'Valor', tag: '{{VALOR_MENSAL}}' },
                                            { label: 'Nossa Empresa', tag: '{{NOME_CONTRATADA}}' },
                                        ].map(v => (
                                            <button
                                                key={v.tag}
                                                type="button"
                                                onClick={() => handleInsertVariableIntoClause(v.tag)}
                                                className="px-2 py-0.5 bg-slate-850 hover:bg-slate-800 border border-slate-700/60 rounded-lg text-[10px] font-mono text-cyan-300 hover:text-cyan-200 transition-colors"
                                                title={`Clique para inserir ${v.tag}`}
                                            >
                                                +{v.label}
                                            </button>
                                        ))}
                                    </div>

                                    <div className="flex justify-end pt-0.5">
                                        <button
                                            type="button"
                                            onClick={handleAddOrUpdateModelClause}
                                            className="w-full py-2.5 px-4 bg-cyan-500 hover:bg-cyan-600 text-white font-black rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center justify-center gap-2"
                                        >
                                            {editingClauseIndex !== null ? (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                                                    Atualizar Cláusula
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                                                    Adicionar ao Modelo
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* Lista de Cláusulas Adicionadas no Modelo */}
                                <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 min-h-[300px]">
                                    <div className="flex justify-between items-center mb-1">
                                        <h4 className="text-xs font-black uppercase tracking-wider text-slate-400">
                                            Cláusulas Deste Modelo ({modelClauses.length})
                                        </h4>
                                        <span className="text-[10px] text-slate-500 italic">Numeração inteligente sequencial</span>
                                    </div>

                                    {modelClauses.length === 0 ? (
                                        <div className="p-8 text-center border-2 border-dashed border-slate-800 rounded-2xl">
                                            <p className="text-xs text-slate-500 font-medium">Nenhuma cláusula adicionada ainda.</p>
                                            <p className="text-[11px] text-slate-600 mt-1">Preencha a seção e o conteúdo acima e clique em "Adicionar ao Modelo".</p>
                                        </div>
                                    ) : (
                                        modelClauses.map((clause, idx) => {
                                            const prevClause = idx > 0 ? modelClauses[idx - 1] : null;
                                            const isNewGroup = clause.group && (!prevClause || prevClause.group !== clause.group);
                                            const numbering = calculateClauseNumbering(modelClauses, idx);
                                            const ordinalTitle = numbering.displayTitle;
                                            const isComp = numbering.isComplementary;
                                            const cleanSubtitle = clause.title && !/^cl[áa]usula\s*\d+[ªº°\.]?\d*/i.test(clause.title.trim()) ? clause.title.trim() : '';

                                            return (
                                                <div key={clause.id || idx} className={`space-y-1.5 ${isComp ? 'pl-4' : ''}`}>
                                                    {isNewGroup && (
                                                        <div className="pt-2 pb-1 flex items-center gap-2 group/grp">
                                                            <span className="h-px bg-slate-800 flex-1"></span>
                                                            <div className="flex items-center gap-1.5 bg-cyan-950/70 border border-cyan-800/50 px-2.5 py-0.5 rounded-full">
                                                                <span className="text-[10px] font-black uppercase tracking-wider text-cyan-400">
                                                                    📁 {clause.group}
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        handleStartEditGroup(clause.group);
                                                                        setIsManageGroupsOpen(true);
                                                                    }}
                                                                    className="text-slate-400 hover:text-amber-300 p-0.5 transition-colors"
                                                                    title={`Renomear grupo "${clause.group}"`}
                                                                >
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDeleteGroup(clause.group)}
                                                                    className="text-slate-400 hover:text-red-400 p-0.5 transition-colors"
                                                                    title={`Excluir grupo "${clause.group}"`}
                                                                >
                                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                                </button>
                                                            </div>
                                                            <span className="h-px bg-slate-800 flex-1"></span>
                                                        </div>
                                                    )}

                                                    <div 
                                                        className={`p-3.5 rounded-2xl border transition-all ${editingClauseIndex === idx ? 'bg-cyan-950/30 border-cyan-500/80 shadow-md' : isComp ? 'bg-amber-950/20 border-amber-800/40 hover:border-amber-700/60' : 'bg-slate-850/80 border-slate-800 hover:border-slate-700'}`}
                                                    >
                                                        <div className="flex justify-between items-start gap-2 mb-1.5">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className={`px-2 py-0.5 rounded-lg border flex items-center justify-center text-[10px] font-black shrink-0 font-mono ${isComp ? 'bg-amber-950/60 border-amber-600/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-cyan-400'}`}>
                                                                    {ordinalTitle}
                                                                </span>
                                                                {isComp && (
                                                                    <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400/90 bg-amber-950/50 px-1.5 py-0.5 rounded border border-amber-800/40">
                                                                        Complementar
                                                                    </span>
                                                                )}
                                                                {cleanSubtitle && (
                                                                    <h5 className="text-xs font-black text-white uppercase tracking-tight line-clamp-1">
                                                                        {cleanSubtitle}
                                                                    </h5>
                                                                )}
                                                            </div>

                                                            <div className="flex items-center gap-1 shrink-0">
                                                                {/* Botão de Adicionar Complementar Direta */}
                                                                {!isComp && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddComplementaryToModel(idx)}
                                                                        className="px-2 py-0.5 rounded-md bg-slate-800 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 border border-slate-700 hover:border-amber-500/40 text-[10px] font-bold transition-all flex items-center gap-1"
                                                                        title={`Adicionar Cláusula Complementar logo após ${ordinalTitle}`}
                                                                    >
                                                                        <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                                                                        <span>+ Complementar</span>
                                                                    </button>
                                                                )}
                                                                {/* Reordenar Subir */}
                                                                <button 
                                                                    type="button"
                                                                    disabled={idx === 0}
                                                                    onClick={() => handleMoveClause(idx, -1)}
                                                                    className="p-1 hover:bg-slate-750 disabled:opacity-30 rounded text-slate-400 hover:text-white transition-colors"
                                                                    title="Mover para cima"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7"/></svg>
                                                                </button>
                                                                {/* Reordenar Descer */}
                                                                <button 
                                                                    type="button"
                                                                    disabled={idx === modelClauses.length - 1}
                                                                    onClick={() => handleMoveClause(idx, 1)}
                                                                    className="p-1 hover:bg-slate-750 disabled:opacity-30 rounded text-slate-400 hover:text-white transition-colors"
                                                                    title="Mover para baixo"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/></svg>
                                                                </button>
                                                                {/* Editar */}
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => handleEditModelClause(idx)}
                                                                    className="p-1 hover:bg-cyan-950 rounded text-cyan-400 hover:text-cyan-300 transition-colors ml-1"
                                                                    title="Editar Cláusula"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                                </button>
                                                                {/* Excluir */}
                                                                <button 
                                                                    type="button"
                                                                    onClick={() => handleDeleteModelClause(idx)}
                                                                    className="p-1 hover:bg-red-950 rounded text-red-400 hover:text-red-300 transition-colors"
                                                                    title="Remover do Modelo"
                                                                >
                                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                                </button>
                                                            </div>
                                                        </div>

                                                        <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">
                                                            {clause.text}
                                                        </p>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                            </div>

                            {/* COLUNA DIREITA: Visualização ao Vivo do Contrato (6 ou 7 colunas) */}
                            <div className="lg:col-span-6 xl:col-span-7 flex flex-col h-full min-h-0 bg-slate-950/70 overflow-hidden">
                                <div className="p-4 border-b border-slate-800 flex justify-between items-center bg-slate-900/60 shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
                                            Visualização em Tempo Real (Documento Final)
                                        </h3>
                                    </div>
                                    <span className="text-[11px] text-slate-500 font-mono bg-slate-800 px-2 py-0.5 rounded border border-slate-700">
                                        Folha de Contrato Oficial
                                    </span>
                                </div>

                                <div className="flex-1 overflow-y-auto p-4 sm:p-8 flex justify-center items-start">
                                    {/* Folha Estilo Documento Oficial */}
                                    <div className="w-full max-w-2xl bg-white text-slate-900 p-8 sm:p-12 rounded-xl shadow-2xl border border-slate-200 h-fit min-h-full shrink-0 my-auto">
                                        {/* Cabeçalho do Documento com Logotipo à Esquerda e Título Logo Abaixo */}
                                        <div className="pb-6 mb-8 border-b-2 border-slate-800">
                                            {templateLogo ? (
                                                <div className="mb-5">
                                                    <div className="flex items-center justify-between gap-4 mb-2">
                                                        <div className="relative group inline-block">
                                                            <img 
                                                                src={templateLogo} 
                                                                alt="Logo" 
                                                                style={{ width: `${templateLogoWidth}px`, maxWidth: '100%', height: 'auto' }}
                                                                className="object-contain block" 
                                                            />
                                                            <button 
                                                                type="button" 
                                                                onClick={() => setTemplateLogo('')}
                                                                className="absolute -top-2 -right-4 bg-rose-500 hover:bg-rose-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                                                title="Remover logotipo"
                                                            >
                                                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
                                                            </button>
                                                        </div>

                                                        {/* Mecanismo para ajustar o tamanho do Logo */}
                                                        <div className="flex items-center gap-2 bg-slate-100 px-3 py-1.5 rounded-lg border border-slate-300">
                                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                                Tamanho Logo:
                                                            </span>
                                                            <input 
                                                                type="range" 
                                                                min="80" 
                                                                max="320" 
                                                                step="10"
                                                                value={templateLogoWidth}
                                                                onChange={(e) => setTemplateLogoWidth(Number(e.target.value))}
                                                                className="w-24 h-1.5 bg-slate-300 rounded-lg appearance-none cursor-pointer accent-cyan-600"
                                                                title={`Largura: ${templateLogoWidth}px`}
                                                            />
                                                            <span className="text-[10px] font-mono font-bold text-slate-700 min-w-[36px]">
                                                                {templateLogoWidth}px
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="mb-5 flex justify-start">
                                                    <label className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-cyan-500 bg-slate-50 hover:bg-cyan-50/40 px-4 py-2.5 rounded-xl transition-all inline-flex items-center gap-2 group" title="Clique para anexar logotipo na esquerda do cabeçalho">
                                                        <svg className="w-4 h-4 text-slate-400 group-hover:text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                                                        <div>
                                                            <span className="text-[11px] font-black uppercase tracking-wider text-slate-600 group-hover:text-cyan-600 block leading-tight">
                                                                + Configurar Logotipo do Contrato
                                                            </span>
                                                            <span className="text-[9px] text-slate-400 block leading-tight">
                                                                Fica no canto esquerdo superior acima do documento
                                                            </span>
                                                        </div>
                                                        <input 
                                                            type="file" 
                                                            accept="image/*" 
                                                            className="hidden" 
                                                            onChange={(e) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file) return;
                                                                const reader = new FileReader();
                                                                reader.onload = (event) => {
                                                                    setTemplateLogo(event.target.result);
                                                                };
                                                                reader.readAsDataURL(file);
                                                            }}
                                                        />
                                                    </label>
                                                </div>
                                            )}

                                            <h1 className="text-center text-lg sm:text-xl font-black uppercase tracking-tight text-slate-900 leading-tight">
                                                {templateName || 'TÍTULO DO MODELO DE CONTRATO'}
                                            </h1>
                                        </div>

                                        {/* Corpo do Contrato com Qualificação das Partes e Cláusulas */}
                                        <div className="space-y-6 text-sm text-slate-800">
                                            {/* Qualificação das Partes (Cabeçalho Formal) */}
                                            <div className="space-y-3 text-[13px] text-justify leading-relaxed text-slate-700 pb-4 border-b border-slate-200">
                                                <p>
                                                    <strong>CONTRATANTE:</strong> <span className="font-mono text-cyan-800 font-bold">&#123;&#123;NOME_CONTRATANTE&#125;&#125;</span>, com sede em <span className="font-mono text-cyan-800">&#123;&#123;ENDERECO_CONTRATANTE&#125;&#125;</span>, inscrita no CNPJ/CPF sob o nº <span className="font-mono text-cyan-800 font-bold">&#123;&#123;CNPJ_CONTRATANTE&#125;&#125;</span>, representada nos termos do seu contrato social.
                                                </p>
                                                <p>
                                                    <strong>CONTRATADA:</strong> <strong>ARCUS IT NETWORK SOLUTIONS TI Ltda - ME</strong>, com sede em Indaiatuba, na RUA TABAJARA VALERIANO DE CARVALHO, 263, JARDIM DO VALE 2, CEP: 13345-760, no Estado SÃO PAULO, inscrita no C.N.P.J. sob o nº 09.407.067/0001-10, no Cadastro Estadual sob o nº 353.260.204.115 e CCM nº 127.402-6, neste ato representada pelo seu diretor Jonathan Henrique Dias de Oliveira, brasileiro, casado, Carteira de Identidade nº 47.929.414-8, C.P.F. nº 400.323.008-05, residente e domiciliado na RUA Moema, 236, Jardim Paulista, Indaiatuba - SP.
                                                </p>
                                                <p className="italic text-slate-600 pt-1">
                                                    As partes acima identificadas têm, entre si, justas e acertadas o presente Contrato de Prestação de Serviços de Manutenção e Gestão de TI, que se regerá pelas cláusulas seguintes e pelas condições descritas no presente.
                                                </p>
                                            </div>

                                            {/* Cláusulas Organizadas por Grupo */}
                                            {modelClauses.length === 0 ? (
                                                <div className="py-12 text-center text-slate-400">
                                                    <svg className="w-10 h-10 mx-auto text-slate-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                                    <p className="text-xs font-semibold text-slate-500">As cláusulas criadas à esquerda aparecerão aqui em tempo real.</p>
                                                </div>
                                            ) : (
                                                modelClauses.map((clause, idx) => {
                                                    const prevClause = idx > 0 ? modelClauses[idx - 1] : null;
                                                    const showGroupHeader = clause.group && (!prevClause || prevClause.group !== clause.group);

                                                    const numbering = calculateClauseNumbering(modelClauses, idx);
                                                    const dynamicTitle = numbering.displayTitle;
                                                    const isComp = numbering.isComplementary;
                                                    const extraSubtitle = clause.title && !/^cl[áa]usula\s*\d+[ªº°\.]?\d*/i.test(clause.title.trim()) ? ` - ${clause.title.trim()}` : '';
                                                    const fullTitleDisplay = `${dynamicTitle}${extraSubtitle}`;

                                                    return (
                                                        <div key={clause.id || idx} className={`space-y-2 ${isComp ? 'ml-5 pl-3 border-l-2 border-slate-300' : ''}`}>
                                                            {showGroupHeader && (
                                                                <div className="pt-4 pb-1 border-b border-slate-300 mb-2">
                                                                    <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm font-sans">
                                                                        {clause.group}
                                                                    </h3>
                                                                </div>
                                                            )}
                                                            <div className="text-[13px] leading-relaxed text-justify text-slate-800">
                                                                <strong className="font-black text-slate-900 mr-2 uppercase">
                                                                    {fullTitleDisplay}.
                                                                </strong>
                                                                <span className="font-serif text-slate-700 whitespace-pre-wrap">
                                                                    {clause.text}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}

                                            {/* Fecho e Datação */}
                                            <div className="pt-8 text-xs text-slate-600 leading-relaxed border-t border-slate-200">
                                                <p className="text-justify mb-4">
                                                    Por estarem assim justos e contratados, firmam o presente instrumento em vias eletrônicas de igual teor e validade jurídica.
                                                </p>
                                                <p className="text-center font-bold text-slate-700 my-4">
                                                    Indaiatuba, {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}.
                                                </p>
                                            </div>

                                            {/* Rodapé Oficial das Partes (Arcus e Cliente) */}
                                            <div className="pt-6 grid grid-cols-2 gap-8 text-center text-xs text-slate-600">
                                                <div>
                                                    <div className="border-b border-slate-400 mb-2 pb-8"></div>
                                                    <p className="font-bold uppercase text-slate-800 text-[11px]">ARCUS IT NETWORK SOLUTIONS TI Ltda - ME</p>
                                                    <p className="text-[10px] text-slate-500">CONTRATADA</p>
                                                </div>
                                                <div>
                                                    <div className="border-b border-slate-400 mb-2 pb-8"></div>
                                                    <p className="font-bold uppercase text-slate-800 text-[11px]">&#123;&#123;NOME_CONTRATANTE&#125;&#125;</p>
                                                    <p className="text-[10px] text-slate-500">CONTRATANTE</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* FORMULÁRIO DE CONTRATO (MODAL COM CONFIGURAÇÃO À ESQUERDA E PREVIEW A4 À DIREITA) */}
            {isContractFormOpen && (
                <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex justify-center items-center z-[150] p-1 sm:p-2 md:p-3 animate-fadeIn">
                    <div className="bg-slate-900 rounded-3xl border border-slate-700/80 shadow-2xl w-full max-w-[98vw] 2xl:max-w-[1720px] h-[96vh] flex flex-col overflow-hidden animate-scaleIn">
                        {/* Header Superior */}
                        <div className="p-4 sm:p-5 border-b border-slate-800 flex justify-between items-center bg-slate-900/95 shrink-0">
                            <div className="flex items-center gap-3">
                                <span className="p-2 bg-cyan-500/10 text-cyan-400 rounded-xl border border-cyan-500/20">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                </span>
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-black text-cyan-400 uppercase tracking-tight">
                                        {currentContract ? 'Nova Versão do Contrato' : 'Vincular Novo Contrato de Cliente'}
                                    </h2>
                                    <p className="text-xs text-slate-400 mt-0.5">
                                        Configure os dados e variáveis à esquerda para visualizar o documento oficial sendo montado em tempo real à direita.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => setIsContractFormOpen(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 font-bold text-xs uppercase tracking-wider transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSaveContract}
                                    className="px-5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-600 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-cyan-500/25 transition-all flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                                    {currentContract ? 'Salvar Nova Versão' : 'Vincular Contrato'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsContractFormOpen(false)}
                                    className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors ml-1"
                                    title="Fechar"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
                                </button>
                            </div>
                        </div>

                        {/* Corpo Split: Esquerda Formulário, Direita Preview Folha A4 */}
                        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 min-h-0 overflow-hidden">
                            {/* COLUNA ESQUERDA: Formulário de Configuração, Variáveis e Cláusulas Autônomas (5 colunas) */}
                            <div className="lg:col-span-5 xl:col-span-5 border-r border-slate-800 flex flex-col h-full min-h-0 overflow-hidden bg-slate-900/60">
                                {/* Navegação por Abas: Dados & Variáveis vs Personalizar Cláusulas */}
                                <div className="p-3 border-b border-slate-800 bg-slate-950/80 flex gap-2 shrink-0">
                                    <button
                                        type="button"
                                        onClick={() => setContractFormActiveTab('variables')}
                                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${contractFormActiveTab === 'variables' ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25' : 'bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                                        <span>Dados & Variáveis</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setContractFormActiveTab('clauses')}
                                        className={`flex-1 py-2 px-3 rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${contractFormActiveTab === 'clauses' ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/25' : 'bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-800'}`}
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                        <span>Cláusulas deste Contrato ({contractClauses.length})</span>
                                    </button>
                                </div>

                                {contractFormActiveTab === 'variables' ? (
                                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-5 space-y-5">
                                        <div>
                                            <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-2">Filtrar Clientes por Tipo</label>
                                            <div className="flex gap-2 mb-3 bg-slate-950 p-1.5 rounded-xl border border-slate-800">
                                                <button 
                                                    type="button" 
                                                    onClick={() => setSelectedClientType('Contrato')} 
                                                    className={`flex-1 py-2 text-xs font-bold rounded-lg uppercase tracking-wider transition-all ${selectedClientType === 'Contrato' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                                                >
                                                    Clientes Contrato
                                                </button>
                                                <button 
                                                    type="button" 
                                                    onClick={() => setSelectedClientType('Avulso')} 
                                                    className={`flex-1 py-2 text-xs font-bold rounded-lg uppercase tracking-wider transition-all ${selectedClientType === 'Avulso' ? 'bg-cyan-500 text-white shadow-md' : 'text-slate-400 hover:text-slate-200'}`}
                                                >
                                                    Clientes Avulsos
                                                </button>
                                            </div>

                                            <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-1.5">Cliente</label>
                                            <select 
                                                value={selectedClient}
                                                onChange={handleClientChange}
                                                className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-3.5 text-sm text-white focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                                                disabled={!!currentContract || loadingClients}
                                                required
                                            >
                                                <option value="">Selecione um cliente ({selectedClientType === 'Contrato' ? 'Contrato' : 'Avulso'})...</option>
                                                {clients.filter(c => c.type === selectedClientType).map(c => (
                                                    <option key={c.id} value={c.id}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <div className="flex justify-between items-center mb-1.5">
                                                <label className="block text-xs font-black uppercase tracking-wider text-slate-400">Modelo Base Padrão (Ponto de Partida)</label>
                                                {selectedTemplate && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleApplyTemplateVariables(selectedTemplate, selectedClient, contractValMaquinas, contractValMensal)}
                                                        className="text-[10px] font-bold text-cyan-400 hover:text-cyan-300 flex items-center gap-1 transition-colors"
                                                        title="Recarregar cláusulas do modelo base para este contrato"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
                                                        Recarregar do Modelo
                                                    </button>
                                                )}
                                            </div>
                                            <select 
                                                value={selectedTemplate}
                                                onChange={handleTemplateChange}
                                                className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-3.5 text-sm text-white focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                                            >
                                                <option value="">Nenhum (Começar em branco)</option>
                                                {templates.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                            <p className="text-[10px] text-cyan-400/80 mt-1">
                                                💡 O modelo serve como gabarito inicial. Você pode personalizar ou apagar qualquer cláusula na aba ao lado sem alterar o modelo base original.
                                            </p>
                                        </div>

                                        {/* NOME / SERVIÇO DE IDENTIFICAÇÃO DO CONTRATO */}
                                        <div className="bg-slate-950/70 p-4 rounded-2xl border border-slate-800/80">
                                            <div className="flex justify-between items-center mb-1.5">
                                                <label className="block text-xs font-black uppercase tracking-wider text-cyan-400">
                                                    Nome de Identificação (Exibido no Chamado)
                                                </label>
                                                <span className="text-[10px] text-slate-500 font-mono">Dropdown Portal</span>
                                            </div>
                                            <input 
                                                type="text"
                                                value={contractNameInput}
                                                onChange={(e) => {
                                                    setContractNameInput(e.target.value);
                                                    recompileContractWithClauses(contractClauses, selectedClient, e.target.value);
                                                }}
                                                placeholder="Ex: Helpdesk, Suporte Mensal, etc."
                                                className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3.5 py-2.5 text-xs text-white font-medium focus:ring-2 focus:ring-cyan-400 focus:outline-none placeholder-slate-500"
                                                required
                                            />
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                Nome que aparecerá na seleção ao cliente abrir chamados.
                                            </p>
                                        </div>

                                        {/* CAMPOS DINÂMICOS / DADOS DO CONTRATO */}
                                        <div className="bg-slate-950 p-5 rounded-3xl border border-slate-800 space-y-3.5">
                                            <h3 className="text-xs font-black uppercase tracking-wider text-cyan-400">Variáveis do Contrato</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 mb-1">Quantidade de Máquinas</label>
                                                    <input 
                                                        type="number"
                                                        value={contractValMaquinas}
                                                        onChange={(e) => handleVariablesChange(e.target.value, contractValMensal)}
                                                        className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-bold text-slate-400 mb-1">Valor do Contrato (R$)</label>
                                                    <input 
                                                        type="text"
                                                        value={contractValMensal}
                                                        onChange={(e) => handleVariablesChange(contractValMaquinas, e.target.value)}
                                                        className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-2 pt-1">
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 mb-0.5">Nossa Razão Social</label>
                                                    <input 
                                                        type="text"
                                                        value={ourCompanyName}
                                                        onChange={(e) => {
                                                            setOurCompanyName(e.target.value);
                                                            recompileContractWithClauses(contractClauses, selectedClient, contractNameInput, contractLogo, contractLogoWidth, contractValMaquinas, contractValMensal, e.target.value, ourCompanyCnpj, ourCompanyAddress);
                                                        }}
                                                        className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 mb-0.5">Nosso CNPJ</label>
                                                    <input 
                                                        type="text"
                                                        value={ourCompanyCnpj}
                                                        onChange={(e) => {
                                                            setOurCompanyCnpj(e.target.value);
                                                            recompileContractWithClauses(contractClauses, selectedClient, contractNameInput, contractLogo, contractLogoWidth, contractValMaquinas, contractValMensal, ourCompanyName, e.target.value, ourCompanyAddress);
                                                        }}
                                                        className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 mb-0.5">Nosso Endereço</label>
                                                    <input 
                                                        type="text"
                                                        value={ourCompanyAddress}
                                                        onChange={(e) => {
                                                            setOurCompanyAddress(e.target.value);
                                                            recompileContractWithClauses(contractClauses, selectedClient, contractNameInput, contractLogo, contractLogoWidth, contractValMaquinas, contractValMensal, ourCompanyName, ourCompanyCnpj, e.target.value);
                                                        }}
                                                        className="w-full bg-slate-900 border border-slate-800 rounded-xl p-2.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-cyan-500"
                                                    />
                                                </div>
                                            </div>
                                            <div className="text-[9.5px] text-slate-500 italic">
                                                * Atualiza automaticamente os dados e valores no preview ao lado em tempo real.
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-black uppercase tracking-wider text-slate-400 mb-1.5">Anotações / Descrição desta Versão</label>
                                            <input 
                                                type="text"
                                                value={contractNotes}
                                                onChange={(e) => setContractNotes(e.target.value)}
                                                placeholder="Ex: Ajuste de cláusula ou criação inicial"
                                                className="w-full bg-slate-800 border border-slate-700 rounded-2xl p-3.5 text-xs text-white focus:ring-2 focus:ring-cyan-400 focus:outline-none"
                                                required
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    /* ABA 2: EDITOR E GERENCIADOR EXCLUSIVO DE CLÁUSULAS DESTE CONTRATO */
                                    <div className="flex-1 min-h-0 flex flex-col h-full overflow-hidden">
                                        {/* Editor / Adicionar Cláusula */}
                                        <div className="p-4 border-b border-slate-800 bg-slate-950/60 shrink-0 space-y-2.5">
                                            <div className="flex justify-between items-center">
                                                <h3 className="text-xs font-black uppercase tracking-wider text-cyan-400 flex items-center gap-1.5">
                                                    <span>{editingContractClauseIndex !== null ? '✏️ Editar Cláusula Deste Contrato' : '➕ Nova Cláusula Exclusiva'}</span>
                                                </h3>
                                                {editingContractClauseIndex !== null && (
                                                    <button
                                                        type="button"
                                                        onClick={handleCancelEditContractClause}
                                                        className="text-[11px] font-bold text-slate-400 hover:text-slate-200 underline"
                                                    >
                                                        Cancelar Edição
                                                    </button>
                                                )}
                                            </div>

                                            {/* Seleção de Seção / Grupo para o Contrato */}
                                            <div>
                                                <div className="flex justify-between items-center mb-1">
                                                    <label className="block text-[11px] font-black text-cyan-400 uppercase tracking-wider">
                                                        Grupo / Seção
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setIsContractCustomGroup(!isContractCustomGroup);
                                                            if (!isContractCustomGroup) setContractCustomGroupName('');
                                                        }}
                                                        className="text-[10px] font-bold text-cyan-400 hover:text-cyan-300 underline flex items-center gap-1"
                                                    >
                                                        {isContractCustomGroup ? '← Escolher Grupo Existente' : '+ Criar Novo Grupo'}
                                                    </button>
                                                </div>

                                                {isContractCustomGroup ? (
                                                    <div className="flex gap-2">
                                                        <input 
                                                            type="text"
                                                            value={contractCustomGroupName}
                                                            onChange={(e) => setContractCustomGroupName(e.target.value)}
                                                            placeholder="Ex: DAS CONDIÇÕES ESPECIAIS DESTE CLIENTE"
                                                            className="w-full bg-slate-800 border-2 border-cyan-500 rounded-xl px-3.5 py-2 text-xs text-white font-bold placeholder-slate-500 focus:outline-none uppercase"
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsContractCustomGroup(false)}
                                                            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl"
                                                        >
                                                            Voltar
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <select 
                                                        value={contractClauseInputGroup}
                                                        onChange={(e) => {
                                                            if (e.target.value === '__NEW_CONTRACT_GROUP__') {
                                                                setIsContractCustomGroup(true);
                                                                setContractCustomGroupName('');
                                                            } else {
                                                                setContractClauseInputGroup(e.target.value);
                                                            }
                                                        }}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-2 text-xs text-white font-bold focus:ring-2 focus:ring-cyan-400 focus:outline-none uppercase cursor-pointer"
                                                    >
                                                        <option value="">Selecione o Grupo / Seção...</option>
                                                        {Array.from(new Set([...contractCustomGroups, ...contractClauses.map(c => c.group).filter(Boolean)])).map(g => (
                                                            <option key={g} value={g}>📁 {g}</option>
                                                        ))}
                                                        <option value="__NEW_CONTRACT_GROUP__">+ Outro Grupo / Nova Seção...</option>
                                                    </select>
                                                )}
                                            </div>

                                            {/* Tipo de Cláusula: Principal vs Complementar para o Contrato */}
                                            <div className="bg-slate-900/90 p-2 rounded-xl border border-slate-800">
                                                <label className="block text-[10px] font-black text-cyan-400 uppercase tracking-wider mb-1.5">
                                                    Tipo de Cláusula
                                                </label>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsContractClauseComplementary(false)}
                                                        className={`py-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${!isContractClauseComplementary ? 'bg-cyan-500 text-white shadow-md' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                                                    >
                                                        <span>📋 Principal</span>
                                                        <span className="text-[10px] opacity-75 font-mono">(ex: Cláusula 10ª)</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsContractClauseComplementary(true)}
                                                        className={`py-1.5 px-2.5 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${isContractClauseComplementary ? 'bg-amber-500 text-white shadow-md' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                                                    >
                                                        <span>↳ Complementar</span>
                                                        <span className="text-[10px] opacity-75 font-mono">(ex: Cláusula 10.1)</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Subtítulo Opcional da Cláusula */}
                                            <div>
                                                <div className="flex justify-between items-center mb-1">
                                                    <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                        {isContractClauseComplementary ? 'Identificação Complementar' : 'Numeração & Subtítulo'}
                                                    </label>
                                                    <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/60 px-2 py-0.5 rounded border border-cyan-800/40">
                                                        {(() => {
                                                            if (editingContractClauseIndex !== null) {
                                                                const num = calculateClauseNumbering(contractClauses, editingContractClauseIndex);
                                                                return `Editando: ${num.displayTitle}`;
                                                            }
                                                            if (isContractClauseComplementary) {
                                                                const simulated = [...contractClauses, { isComplementary: true }];
                                                                const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                                return `Próxima: ${num.displayTitle}`;
                                                            }
                                                            const simulated = [...contractClauses, { isComplementary: false }];
                                                            const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                            return `Próxima: ${num.displayTitle}`;
                                                        })()}
                                                    </span>
                                                </div>
                                                <div className="flex gap-2 items-center">
                                                    <span className={`px-3 py-1.5 rounded-xl text-xs font-black font-mono shrink-0 border ${isContractClauseComplementary ? 'bg-amber-950/60 border-amber-600/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-cyan-400'}`}>
                                                        {(() => {
                                                            if (editingContractClauseIndex !== null) {
                                                                const num = calculateClauseNumbering(contractClauses, editingContractClauseIndex);
                                                                return num.displayTitle;
                                                            }
                                                            if (isContractClauseComplementary) {
                                                                const simulated = [...contractClauses, { isComplementary: true }];
                                                                const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                                return num.displayTitle;
                                                            }
                                                            const simulated = [...contractClauses, { isComplementary: false }];
                                                            const num = calculateClauseNumbering(simulated, simulated.length - 1);
                                                            return num.displayTitle;
                                                        })()}
                                                    </span>
                                                    <input 
                                                        type="text"
                                                        value={contractClauseInputTitle}
                                                        onChange={(e) => setContractClauseInputTitle(e.target.value)}
                                                        placeholder={isContractClauseComplementary ? "Subtítulo opcional da complementar" : "Subtítulo opcional (Ex: DAS PENALIDADES)"}
                                                        className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3.5 py-1.5 text-xs text-white font-bold placeholder-slate-500 focus:ring-2 focus:ring-cyan-400 focus:outline-none uppercase"
                                                    />
                                                </div>
                                            </div>

                                            {/* Texto da Cláusula */}
                                            <div>
                                                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                                                    Texto da Cláusula
                                                </label>
                                                <textarea 
                                                    rows="3"
                                                    value={contractClauseInputText}
                                                    onChange={(e) => setContractClauseInputText(e.target.value)}
                                                    placeholder="Digite o texto da cláusula... Você pode usar variáveis dinâmicas abaixo."
                                                    className="w-full bg-slate-800 border border-slate-700 rounded-xl p-2.5 text-xs text-white placeholder-slate-500 focus:ring-2 focus:ring-cyan-400 focus:outline-none resize-none leading-relaxed"
                                                />
                                            </div>

                                            {/* Tags Dinâmicas */}
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">Variáveis:</span>
                                                {[
                                                    { label: 'Cliente', tag: '{{NOME_CONTRATANTE}}' },
                                                    { label: 'CNPJ', tag: '{{CNPJ_CONTRATANTE}}' },
                                                    { label: 'Endereço', tag: '{{ENDERECO_CONTRATANTE}}' },
                                                    { label: 'Máquinas', tag: '{{QTD_MAQUINAS}}' },
                                                    { label: 'Valor', tag: '{{VALOR_MENSAL}}' },
                                                    { label: 'Nossa Empresa', tag: '{{NOME_CONTRATADA}}' },
                                                ].map(v => (
                                                    <button
                                                        key={v.tag}
                                                        type="button"
                                                        onClick={() => handleInsertVariableIntoContractClause(v.tag)}
                                                        className="px-2 py-0.5 bg-slate-850 hover:bg-slate-800 border border-slate-700/60 rounded-lg text-[10px] font-mono text-cyan-300 hover:text-cyan-200 transition-colors"
                                                        title={`Clique para inserir ${v.tag}`}
                                                    >
                                                        +{v.label}
                                                    </button>
                                                ))}
                                            </div>

                                            <div className="flex justify-end pt-0.5">
                                                <button
                                                    type="button"
                                                    onClick={handleAddOrUpdateContractClause}
                                                    className="w-full py-2.5 px-4 bg-cyan-500 hover:bg-cyan-600 text-white font-black rounded-xl text-xs uppercase tracking-wider transition-all shadow-md flex items-center justify-center gap-2"
                                                >
                                                    {editingContractClauseIndex !== null ? (
                                                        <>
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                                                            Salvar Alteração na Cláusula
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                                                            Adicionar Cláusula ao Contrato
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Lista de Cláusulas deste Contrato (Reordenar, Editar, Excluir) */}
                                        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3 custom-scrollbar">
                                            <div className="flex justify-between items-center px-1">
                                                <span className="text-[11px] font-black uppercase tracking-wider text-slate-400">
                                                    Cláusulas Deste Contrato ({contractClauses.length})
                                                </span>
                                                <span className="text-[10px] text-emerald-400 font-bold bg-emerald-950/60 px-2 py-0.5 rounded border border-emerald-800/40">
                                                    ✓ Edição Segura e Independente
                                                </span>
                                            </div>

                                            {contractClauses.length === 0 ? (
                                                <div className="py-12 text-center text-slate-500">
                                                    <p className="text-xs">Nenhuma cláusula cadastrada para este contrato.</p>
                                                    <p className="text-[11px] text-slate-600 mt-1">Selecione um Modelo Base na aba anterior ou adicione cláusulas acima.</p>
                                                </div>
                                            ) : (
                                                contractClauses.map((clause, idx) => {
                                                    const numbering = calculateClauseNumbering(contractClauses, idx);
                                                    const ordinalTitle = numbering.displayTitle;
                                                    const isComp = numbering.isComplementary;
                                                    const cleanSubtitle = clause.title && !/^cl[áa]usula\s*\d+[ªº°\.]?\d*/i.test(clause.title.trim()) ? clause.title.trim() : '';

                                                    return (
                                                        <div 
                                                            key={clause.id || idx}
                                                            className={`p-3 rounded-2xl border transition-all ${isComp ? 'ml-4' : ''} ${editingContractClauseIndex === idx ? 'bg-cyan-950/30 border-cyan-500 ring-1 ring-cyan-500' : isComp ? 'bg-amber-950/20 border-amber-800/40 hover:border-amber-700/60' : 'bg-slate-950/40 border-slate-800/80 hover:border-slate-700'}`}
                                                        >
                                                            {clause.group && (
                                                                <div className="text-[10px] font-black uppercase text-cyan-400 tracking-wider mb-1 flex items-center gap-1">
                                                                    <span>📁</span> {clause.group}
                                                                </div>
                                                            )}
                                                            <div className="flex justify-between items-start gap-2 mb-1.5">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className={`px-2 py-0.5 rounded-lg border flex items-center justify-center text-[10px] font-black shrink-0 font-mono ${isComp ? 'bg-amber-950/60 border-amber-600/50 text-amber-400' : 'bg-slate-900 border-slate-700 text-cyan-400'}`}>
                                                                        {ordinalTitle}
                                                                    </span>
                                                                    {isComp && (
                                                                        <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400/90 bg-amber-950/50 px-1.5 py-0.5 rounded border border-amber-800/40">
                                                                            Complementar
                                                                        </span>
                                                                    )}
                                                                    {cleanSubtitle && (
                                                                        <h5 className="text-xs font-black text-white uppercase tracking-tight line-clamp-1">
                                                                            {cleanSubtitle}
                                                                        </h5>
                                                                    )}
                                                                </div>

                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    {/* Botão de Adicionar Complementar Direta */}
                                                                    {!isComp && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleAddComplementaryToContract(idx)}
                                                                            className="px-2 py-0.5 rounded-md bg-slate-800 hover:bg-amber-500/20 text-amber-400 hover:text-amber-300 border border-slate-700 hover:border-amber-500/40 text-[10px] font-bold transition-all flex items-center gap-1"
                                                                            title={`Adicionar Cláusula Complementar logo após ${ordinalTitle}`}
                                                                        >
                                                                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4"/></svg>
                                                                            <span>+ Complementar</span>
                                                                        </button>
                                                                    )}
                                                                    <button 
                                                                        type="button"
                                                                        disabled={idx === 0}
                                                                        onClick={() => handleMoveContractClause(idx, -1)}
                                                                        className="p-1 hover:bg-slate-750 disabled:opacity-30 rounded text-slate-400 hover:text-white transition-colors"
                                                                        title="Mover para cima"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7"/></svg>
                                                                    </button>
                                                                    <button 
                                                                        type="button"
                                                                        disabled={idx === contractClauses.length - 1}
                                                                        onClick={() => handleMoveContractClause(idx, 1)}
                                                                        className="p-1 hover:bg-slate-750 disabled:opacity-30 rounded text-slate-400 hover:text-white transition-colors"
                                                                        title="Mover para baixo"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"/></svg>
                                                                    </button>
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => handleEditContractClause(idx)}
                                                                        className="p-1 hover:bg-cyan-950 rounded text-cyan-400 hover:text-cyan-300 transition-colors ml-1"
                                                                        title="Editar Cláusula"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                                    </button>
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => handleDeleteContractClause(idx)}
                                                                        className="p-1 hover:bg-red-950 rounded text-red-400 hover:text-red-300 transition-colors"
                                                                        title="Remover deste Contrato"
                                                                    >
                                                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <p className="text-[11px] text-slate-400 line-clamp-2 leading-relaxed">
                                                                {clause.text}
                                                            </p>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* COLUNA DIREITA: Visualização em Tempo Real da Folha Oficial A4 (7 colunas) */}
                            <div className="lg:col-span-7 xl:col-span-7 flex flex-col h-full min-h-0 overflow-hidden bg-slate-950/80">
                                <div className="p-3.5 sm:px-6 border-b border-slate-800/80 bg-slate-900/40 flex justify-between items-center shrink-0">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-pulse"></span>
                                        <h3 className="text-xs font-black uppercase tracking-wider text-slate-300">
                                            📄 Visualização em Tempo Real (Documento Oficial A4)
                                        </h3>
                                    </div>
                                    <span className="text-[10px] text-cyan-400 font-mono bg-cyan-950/60 px-2.5 py-0.5 rounded border border-cyan-800/40">
                                        Folha A4 Oficial
                                    </span>
                                </div>

                                <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-8 flex justify-center items-start custom-scrollbar">
                                    {contractContent ? (
                                        <div className="w-full max-w-3xl bg-white text-slate-900 p-8 sm:p-12 rounded-xl shadow-2xl border border-slate-200 h-fit leading-relaxed select-text">
                                            <div dangerouslySetInnerHTML={{ __html: contractContent }} />
                                        </div>
                                    ) : (
                                        <div className="m-auto p-12 text-center bg-slate-900/60 border border-dashed border-slate-800 rounded-3xl max-w-md">
                                            <div className="text-4xl mb-3">📄</div>
                                            <h4 className="text-sm font-bold text-white mb-1">Pré-visualização do Contrato</h4>
                                            <p className="text-xs text-slate-400">
                                                Selecione um Cliente e um Modelo Base à esquerda para montar e visualizar a folha A4 preenchida em tempo real.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal de Gerenciamento de Grupos / Seções */}
            {isManageGroupsOpen && (
                <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md flex justify-center items-center z-[150] p-4 animate-fadeIn">
                    <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh] animate-scaleIn">
                        {/* Header */}
                        <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-950/60">
                            <div>
                                <h3 className="text-base font-black text-white uppercase tracking-tight flex items-center gap-2">
                                    <span>⚙️ Gerenciar Grupos / Seções</span>
                                </h3>
                                <p className="text-xs text-slate-400 mt-0.5">
                                    Edite os nomes ou remova seções sem cláusulas vinculadas.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsManageGroupsOpen(false);
                                    setEditingGroupOriginalName(null);
                                    setEditGroupNewName('');
                                }}
                                className="p-2 text-slate-400 hover:text-white rounded-xl hover:bg-slate-800 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                        </div>

                        {/* Lista de Grupos */}
                        <div className="p-5 overflow-y-auto space-y-3 flex-1">
                            {(() => {
                                const allGroups = Array.from(new Set([...customGroups, ...modelClauses.map(c => c.group).filter(Boolean)]));
                                if (allGroups.length === 0) {
                                    return (
                                        <div className="p-8 text-center border-2 border-dashed border-slate-800 rounded-2xl">
                                            <p className="text-xs text-slate-500 font-medium">Nenhum grupo criado neste modelo ainda.</p>
                                        </div>
                                    );
                                }

                                return allGroups.map((grp) => {
                                    const clausesCount = modelClauses.filter(c => (c.group || '').trim().toUpperCase() === grp.trim().toUpperCase()).length;
                                    const isEditing = editingGroupOriginalName === grp;

                                    return (
                                        <div 
                                            key={grp} 
                                            className="p-3.5 bg-slate-850/80 rounded-2xl border border-slate-800 flex flex-col gap-2"
                                        >
                                            {isEditing ? (
                                                <div className="flex gap-2 items-center">
                                                    <input 
                                                        type="text"
                                                        value={editGroupNewName}
                                                        onChange={(e) => setEditGroupNewName(e.target.value)}
                                                        className="w-full bg-slate-900 border-2 border-cyan-500 rounded-xl px-3 py-1.5 text-xs text-white font-bold uppercase focus:outline-none"
                                                        autoFocus
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSaveEditGroup(grp)}
                                                        className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-black rounded-xl uppercase shrink-0"
                                                    >
                                                        Salvar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setEditingGroupOriginalName(null);
                                                            setEditGroupNewName('');
                                                        }}
                                                        className="px-2.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl shrink-0"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex items-center justify-between gap-3">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-black text-white uppercase tracking-tight truncate">
                                                                📁 {grp}
                                                            </span>
                                                        </div>
                                                        <span className={`text-[11px] font-bold mt-0.5 inline-block ${clausesCount > 0 ? 'text-cyan-400' : 'text-slate-500'}`}>
                                                            {clausesCount === 1 ? '1 cláusula vinculada' : `${clausesCount} cláusulas vinculadas`}
                                                        </span>
                                                    </div>

                                                    <div className="flex items-center gap-1.5 shrink-0">
                                                        {/* Editar Nome do Grupo */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleStartEditGroup(grp)}
                                                            className="p-1.5 hover:bg-cyan-950 rounded-xl text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1 text-xs font-bold px-2.5 bg-cyan-950/40 border border-cyan-800/40"
                                                            title="Editar nome do grupo"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                                                            <span>Editar</span>
                                                        </button>

                                                        {/* Excluir Grupo */}
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDeleteGroup(grp)}
                                                            className={`p-1.5 rounded-xl transition-colors flex items-center gap-1 text-xs font-bold px-2.5 border ${
                                                                clausesCount > 0 
                                                                    ? 'bg-slate-800/60 border-slate-700/60 text-slate-500 hover:text-slate-400 cursor-pointer' 
                                                                    : 'bg-red-950/40 border-red-800/40 text-red-400 hover:text-red-300 hover:bg-red-950'
                                                            }`}
                                                            title={clausesCount > 0 ? 'Não é possível excluir: contém cláusulas' : 'Excluir grupo vazio'}
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                                                            <span>Excluir</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-800 bg-slate-950/60 flex justify-between items-center">
                            <span className="text-[11px] text-slate-500">
                                💡 Regra: Para excluir um grupo, remova antes suas cláusulas.
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    setIsManageGroupsOpen(false);
                                    setEditingGroupOriginalName(null);
                                    setEditGroupNewName('');
                                }}
                                className="px-5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs"
                            >
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL DE ASSINATURA ELETRÔNICA OFICIAL DA ARCUS */}
            <SignatureModal
                isOpen={isSignatureModalOpen}
                onClose={() => {
                    if (!isSubmittingSignature) {
                        setIsSignatureModalOpen(false);
                        setSigningContractTarget(null);
                        setSigningVersionTarget(null);
                    }
                }}
                onConfirmSignature={handleConfirmArcusSignature}
                signerRole="arcus"
                signerName={userEmail?.toLowerCase().includes('juliano') ? 'Juliano' : 'Jonathan Henrique Dias de Oliveira'}
                signerEmail={userEmail}
                contractTitle={signingContractTarget?.contractName || signingContractTarget?.templateName || 'Contrato de TI'}
                clientName={signingContractTarget?.clientName || 'Cliente'}
                contractVersion={signingVersionTarget?.versionNumber || signingContractTarget?.currentVersion || 1}
                isSubmitting={isSubmittingSignature}
            />
        </div>
    );
}
